// Enhanced Variant Pool - With State Management & Reset Validation
// Prevents the critical business risk of customers getting wrong pricing

class SimpleVariantPool {
  constructor() {
    // Debug mode
    this.debug = localStorage.getItem('poolDebug') === 'true';
    this.debugLog = (...args) => {
      if (this.debug) console.log(...args);
    };
    this.debugLog('🔍 EnhancedVariantPool initialized with debug mode');

    // Telemetry system
    this.telemetry = {
      session: this.generateSessionId(),
      startTime: Date.now(),
      events: [],
      counters: {
        ghostDetections: 0,
        ghostRecoveries: 0,
        variantResets: 0,
        fallbacks: 0,
        successfulOrders: 0,
        failedOrders: 0,
        skuGenerationFailures: 0,
        cloudinaryUrls: 0
      }
    };

    // Initialize telemetry
    this.logTelemetryEvent('system_init', {
      debug: this.debug,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    });

    // DEFENSIVE LAYER 1: Track recently reset variants (prevents CDN cache race conditions)
    // Variants reset within the last 90 seconds should be avoided to allow CDN propagation
    this.recentlyResetVariants = new Map(); // variantId -> resetTimestamp
    this.MIN_VARIANT_AGE_MS = 90000; // 90 seconds minimum age after reset

    // DEFENSIVE LAYER 2: Track variants currently being processed (prevents duplicate pool usage)
    // Prevents race condition where rapid double-add uses same pool variant for both items
    this.pendingVariants = new Set(); // variantIds currently in processOrder flow

    // Glass pool variant IDs (existing glass product)
    this.glassPoolVariants = [
      '55883609702787', '55883609735555', '55883609768323', '55883609833859', '56596635156867',
      '55883610554755', '55883610587523', '55883610653059', '55883610685827', '56596637745539',
      '55883610980739', '55883611013507', '55883611079043', '55883611111811', '55883611144579',
      '55883611210115', '55883611242883', '55883611308419', '55883611373955', '55883611439491',
      '55883611505027', '55883611570563', '55883611668867', '55883611734403', '55883611799939',
      '55883611865475', '55883611898243', '55883611963779', '55883612127619', '55883612193155',
      '55883612258691', '55883612324227', '55883612389763', '55883612422531', '55883612488067',
      '55883612553603', '55883612619139', '55883612651907', '55883612750211', '55883613012355',
      '55883613143427', '55883613340035', '55883613602179', '55883613864323', '55883614126467',
      '55883614257539', '55883614388611', '55883614486915', '55883614585219', '55883614650755'
    ];

    // Brick pool variant IDs (recreated with glass method for reliability)
    this.brickPoolVariants = [
      '56553509323139', // Brick Pool #1
      '56553509355907', // Brick Pool #2
      '56553509388675', // Brick Pool #3
      '56553509421443', // Brick Pool #4
      '56553509519747', // Brick Pool #5
      '56553509552515', // Brick Pool #6
      '56553509585283', // Brick Pool #7
      '56553509618051', // Brick Pool #8
      '56553509650819', // Brick Pool #9
      '56553509683587', // Brick Pool #10
      '56553509749123', // Brick Pool #11
      '56553509781891', // Brick Pool #12
      '56553509814659', // Brick Pool #13
      '56553509847427', // Brick Pool #14
      '56553509880195', // Brick Pool #15
      '56553509912963', // Brick Pool #16
      '56553510011267', // Brick Pool #17
      '56553510044035', // Brick Pool #18
      '56553510076803', // Brick Pool #19
      '56553510109571', // Brick Pool #20
      '56553510142339', // Brick Pool #21
      '56553510437251', // Brick Pool #22
      '56553510502787', // Brick Pool #23
      '56553510535555', // Brick Pool #24
      '56553510568323', // Brick Pool #25
      '56553510601091', // Brick Pool #26
      '56553510666627', // Brick Pool #27
      '56553510699395', // Brick Pool #28
      '56553510732163', // Brick Pool #29
      '56553510830467', // Brick Pool #30
      '56553510863235', // Brick Pool #31
      '56553510994307', // Brick Pool #32
      '56553511027075', // Brick Pool #33
      '56553511059843', // Brick Pool #34
      '56553511092611', // Brick Pool #35
      '56553511125379', // Brick Pool #36
      '56553511190915', // Brick Pool #37
      '56553511223683', // Brick Pool #38
      '56553511256451', // Brick Pool #39
      '56553511289219', // Brick Pool #40
      '56553511321987', // Brick Pool #42 (Pool #41 deleted)
      '56553511387523', // Brick Pool #43
      '56553511420291', // Brick Pool #44
      '56553511453059', // Brick Pool #45
      '56553511485827', // Brick Pool #46
      '56553511518595', // Brick Pool #47
      '56553511551363', // Brick Pool #48
      '56553511584131', // Brick Pool #49
      '56553511715203'  // Brick Pool #50
    ];

    // Set active pool based on product type
    this.poolVariants = this.glassPoolVariants;
    this.updateEndpoint = 'https://flourishing-cactus-81c4c1.netlify.app/.netlify/functions/enhanced-variant-updater';
    this.fallbackToCart = true;
    this.productType = 'glass';
    this.SINGLE_FLIGHT_LOCK_KEY = '__sgr_calculator_order_lock';
    this.SINGLE_FLIGHT_TTL_MS = 45000;

    // Cart cache to prevent API hammering (Throttling)
    this.cartCache = {
      data: null,
      timestamp: 0,
      promise: null,
      TTL: 2500 // 2.5 seconds cache
    };

    // Initialize abandoned cart cleanup system
    this.initializeCleanup();

    // Variant states
    this.STATES = {
      CLEAN: 'CLEAN',           // Ready to use
      IN_USE: 'IN_USE',         // Currently assigned to customer
      PENDING_CLEANUP: 'PENDING_CLEANUP', // Being reset
      QUARANTINED: 'QUARANTINED' // Failed reset, needs manual intervention
    };

    if (this.debug) {
      this.debugLog('🎯 Enhanced pool configured with', this.poolVariants.length, 'variants');
      this.debugLog('📡 Endpoint:', this.updateEndpoint);
    }
  }

  acquireSingleFlightLock(productType = 'glass') {
    const now = Date.now();
    const existingLock = window[this.SINGLE_FLIGHT_LOCK_KEY];

    if (existingLock && existingLock.active && existingLock.expiresAt > now) {
      throw new Error('ORDER_IN_FLIGHT');
    }

    const owner = `${productType}_${now}_${Math.random().toString(36).slice(2, 8)}`;
    window[this.SINGLE_FLIGHT_LOCK_KEY] = {
      active: true,
      owner: owner,
      startedAt: now,
      expiresAt: now + this.SINGLE_FLIGHT_TTL_MS,
      productType: productType
    };

    return owner;
  }

  releaseSingleFlightLock(owner) {
    if (!owner) return;
    const existingLock = window[this.SINGLE_FLIGHT_LOCK_KEY];
    if (existingLock && existingLock.owner === owner) {
      window[this.SINGLE_FLIGHT_LOCK_KEY] = {
        active: false,
        owner: null,
        startedAt: 0,
        expiresAt: 0,
        productType: null
      };
    }
  }

  // ==================== PRICING VALIDATION ====================

  /**
   * Validate pricing against business rules to prevent revenue loss
   * @param {number} price - The calculated price per item
   * @param {string} thickness - The glass/brick thickness (e.g., '4mm')
   * @param {object} dimensions - Dimensions for context
   */
  validatePricing(price, thickness, dimensions) {
    // Minimum price rules per thickness (Price Per Item)
    // Reduced to £8 minimum to avoid blocking legitimate small orders
    const MIN_PRICES = {
      '3mm': 8.00,
      '4mm': 8.00,
      '5mm': 8.00,
      '6mm': 8.00
    };

    // 1. Check minimum price
    // Bricks are much cheaper than glass, so we use a lower minimum for bricks
    const isBrick = this.productType === 'brick';
    const minPrice = isBrick ? 1.00 : (MIN_PRICES[thickness] || 8.00);

    if (price < minPrice - 0.01) {
      console.error(`⚠️ Price validation failed: £${price.toFixed(2)} is below minimum £${minPrice.toFixed(2)} (${isBrick ? 'brick' : thickness})`);
      throw new Error(`Price £${price.toFixed(2)} is below minimum £${minPrice.toFixed(2)}`);
    }

    // 2. Check for zero or negative price
    if (price <= 0.01) {
      throw new Error(`Invalid price £${price.toFixed(2)} detected`);
    }

    // 3. Sanity check for extremely high prices (prevent accidental huge charges)
    // Warn if over £1000 for a single piece (unlikely for stove glass)
    if (price > 1000) {
      console.warn(`⚠️ High price warning: £${price.toFixed(2)} for single item`);
      // We don't block this, just warn, as large orders are possible
    }

    if (this.debug) {
      this.debugLog(`✅ Pricing validated: £${price.toFixed(2)} for ${thickness}`);
    }

    return true;
  }

  // ==================== TELEMETRY SYSTEM ====================

  generateSessionId() {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  logTelemetryEvent(eventType, data = {}) {
    const event = {
      type: eventType,
      timestamp: Date.now(),
      sessionId: this.telemetry.session,
      data: data
    };

    this.telemetry.events.push(event);

    // Keep only last 100 events to prevent memory bloat
    if (this.telemetry.events.length > 100) {
      this.telemetry.events = this.telemetry.events.slice(-100);
    }

    // Structured logging for external monitoring
    console.log('POOL_TELEMETRY:', JSON.stringify(event, null, 2));

    this.debugLog('📊 Telemetry event:', eventType, data);

    // Send important events to dashboard automatically
    const importantEvents = ['order_success', 'order_failure', 'ghost_detection_start', 'cloudinary_sku_used', 'variant_reset_success'];
    if (importantEvents.includes(eventType)) {
      this.sendToAnalyticsDashboard({
        eventType: eventType,
        sessionId: this.telemetry.session,
        timestamp: event.timestamp,
        counters: { ...this.telemetry.counters },
        data: data
      });
    }

    // Lightweight in-browser monitoring hook for operational visibility.
    if (eventType === 'order_failure') {
      this.emitMonitoringHook('process_order_failure', data);
    }
    if (eventType === 'fallback_used') {
      this.emitMonitoringHook('fallback_used', data);
    }
  }

  emitMonitoringHook(metricName, data = {}) {
    try {
      const storageKey = 'calculator_monitoring_metrics';
      const current = JSON.parse(localStorage.getItem(storageKey) || '{}');
      current[metricName] = (current[metricName] || 0) + 1;
      current.lastUpdated = Date.now();
      localStorage.setItem(storageKey, JSON.stringify(current));

      document.dispatchEvent(new CustomEvent('calculator:monitoring', {
        detail: {
          metric: metricName,
          count: current[metricName],
          timestamp: current.lastUpdated,
          data: data
        }
      }));
    } catch (error) {
      this.debugLog('Monitoring hook error:', error);
    }
  }

  incrementCounter(counterName, additionalData = {}) {
    if (this.telemetry.counters.hasOwnProperty(counterName)) {
      this.telemetry.counters[counterName]++;
      this.logTelemetryEvent('counter_increment', {
        counter: counterName,
        newValue: this.telemetry.counters[counterName],
        ...additionalData
      });
    }
  }

  getTelemetrySummary() {
    const sessionDuration = Date.now() - this.telemetry.startTime;
    return {
      sessionId: this.telemetry.session,
      sessionDuration: sessionDuration,
      counters: { ...this.telemetry.counters },
      recentEvents: this.telemetry.events.slice(-10),
      timestamp: new Date().toISOString()
    };
  }

  sendTelemetryBeacon(eventType, data = {}) {
    const telemetryData = {
      eventType: eventType,
      sessionId: this.telemetry.session,
      timestamp: Date.now(),
      counters: { ...this.telemetry.counters },
      data: data
    };

    // Send to analytics dashboard
    this.sendToAnalyticsDashboard(telemetryData);

    // Use beacon API for reliable transmission
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(telemetryData)], { type: 'application/json' });
      navigator.sendBeacon('/analytics/telemetry', blob);
    }

    // Fallback structured logging
    console.log('TELEMETRY_BEACON:', JSON.stringify(telemetryData, null, 2));
  }

  // Send telemetry to analytics dashboard
  async sendToAnalyticsDashboard(telemetryData) {
    try {
      const response = await fetch('https://flourishing-cactus-81c4c1.netlify.app/.netlify/functions/enhanced-analytics-accurate?endpoint=telemetry', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(telemetryData)
      });

      if (response.ok) {
        this.debugLog('📊 Telemetry sent to dashboard successfully');
      } else {
        this.debugLog('⚠️ Failed to send telemetry to dashboard:', response.status);
      }
    } catch (error) {
      this.debugLog('⚠️ Telemetry dashboard error:', error);
      // Don't throw - telemetry shouldn't break main functionality
    }
  }

  // Get variant state from localStorage
  getVariantState(variantId) {
    const stateKey = `variant_state_${variantId}`;
    return localStorage.getItem(stateKey) || this.STATES.CLEAN;
  }

  // Set variant state in localStorage (legacy method, now uses timestamps)
  setVariantState(variantId, state) {
    this.setVariantStateWithTimestamp(variantId, state);
  }

  // Get default clean state for a variant
  getDefaultVariantState(variantId, productType = 'glass') {
    const poolIndex = this.poolVariants.indexOf(variantId) + 1;
    const prefix = productType === 'brick' ? 'Brick' : 'Glass';

    return {
      title: `Custom ${prefix} Pool #${poolIndex}`,
      price: '0.01',
      sku: `POOL-${poolIndex.toString().padStart(3, '0')}`
    };
  }

  // Set variant state with timestamp for abandoned cart detection
  setVariantStateWithTimestamp(variantId, state) {
    const stateKey = `variant_state_${variantId}`;
    const timestampKey = `variant_timestamp_${variantId}`;

    localStorage.setItem(stateKey, state);
    localStorage.setItem(timestampKey, Date.now().toString());

    if (this.debug) {
      this.debugLog(`🏷️ Variant ${variantId} state: ${state} (with timestamp)`);
    }
  }

  // Get variant state with timestamp
  getVariantStateWithTimestamp(variantId) {
    const stateKey = `variant_state_${variantId}`;
    const timestampKey = `variant_timestamp_${variantId}`;

    const state = localStorage.getItem(stateKey) || this.STATES.CLEAN;
    const timestamp = localStorage.getItem(timestampKey);

    return {
      state: state,
      timestamp: timestamp ? parseInt(timestamp) : Date.now()
    };
  }

  // Initialize cleanup system - runs periodically to prevent abandoned cart buildup
  initializeCleanup() {
    // Detect if we're on the cart page.
    // IMPORTANT: cart contamination cleanup mutates cart state; keep it cart-page only
    // to avoid collisions with active add-to-cart actions on product/collection pages.
    const isCartPage = window.location.pathname.includes('/cart');

    if (isCartPage) {
      // Cart page: Run cleanup IMMEDIATELY (no delay) - must catch contaminated items before checkout
      this.debugLog('🛒 Cart page detected - running immediate contamination cleanup');
      this.cleanupContaminatedCartItems().then((removedCount) => {
        if (removedCount > 0) {
          this.debugLog(`✅ Removed ${removedCount} items on cart page - forcing reload`);
          window.location.reload();
        } else {
          this.debugLog('✅ Cart page cleanup complete - no items removed');
          // Show recovery banner for previously-expired items (survives the reload above via localStorage)
          this.renderExpiredRecoveryBanner();
        }
      });
    }

    // Run variant cleanup after a delay
    setTimeout(() => this.cleanupAbandonedVariants(), 2000);

    // Set up periodic cleanup every 5 minutes.
    // Contaminated cart cleanup is cart-page only.
    if (isCartPage) {
      setInterval(() => this.cleanupContaminatedCartItems(), 5 * 60 * 1000);
    }
    setInterval(() => this.cleanupAbandonedVariants(), 5 * 60 * 1000);

    this.debugLog('🧹 Abandoned cart cleanup system initialized');
  }

  // NEW: Cart Item Cleanup - Remove pool variants with £0.01 pricing from customer's cart
  // This prevents contaminated items from previous sessions from being ordered
  // Returns number of items removed
  async cleanupContaminatedCartItems() {
    try {
      this.debugLog('🛒 Starting cart contamination cleanup...');

      // Fetch current cart using throttled method
      const cart = await this.getThrottledCart();
      if (!cart.items || cart.items.length === 0) {
        this.debugLog('🛒 Cart is empty, no cleanup needed');
        return 0;
      }

      // Combine all pool variants for checking
      const allPoolVariants = [...this.glassPoolVariants, ...this.brickPoolVariants];

      // Find contaminated items (pool variants with suspicious pricing/SKU)
      // Covers: nightly 3AM reset, multi-day stale carts, and cross-customer overwrite
      // (another buyer reuses the same pool variant and overwrites price/SKU).
      const contaminatedItems = cart.items.filter(item => {
        const variantIdStr = item.variant_id.toString();
        const isPoolVariant = allPoolVariants.includes(variantIdStr);

        if (!isPoolVariant) return false;

        // Check for contamination indicators
        const priceInPounds = item.price / 100;
        const isSuspiciousPrice = priceInPounds === 0.01 || priceInPounds < 1;
        const isSuspiciousSKU = item.sku && (
          item.sku.startsWith('POOL-') ||
          item.sku.startsWith('BRICK-POOL-') ||
          item.sku === ''
        );

        // Expired multi-day cart: variant was reset/reused after add, so the live
        // price no longer matches the price quoted at add time (_Expected Price).
        // SKU-only lag with correct price is acceptable (CDN) - only flag when
        // the price itself disagrees.
        let expectedPriceMismatch = false;
        try {
          const props = item.properties || {};
          const expected = props['_Expected Price'] || props['_Expected price'];
          if (expected !== undefined && expected !== null && expected !== '') {
            const expectedNum = parseFloat(expected);
            if (Number.isFinite(expectedNum)) {
              expectedPriceMismatch = Math.abs(priceInPounds - expectedNum) > 0.01;
            }
          }
        } catch (e) { /* ignore - treat as not mismatched */ }

        return isSuspiciousPrice || isSuspiciousSKU || expectedPriceMismatch;
      });

      if (contaminatedItems.length === 0) {
        this.debugLog('✅ No contaminated pool items found in cart');
        return 0;
      }

      this.debugLog(`🚨 Found ${contaminatedItems.length} contaminated pool items in cart`, contaminatedItems);

      // Preserve sizing spec BEFORE removal so the customer is not sent back to
      // square one. Recovery survives the reload via localStorage.
      this.saveExpiredRecovery(contaminatedItems);

      // Remove each contaminated item
      for (const item of contaminatedItems) {
        try {
          this.debugLog(`🗑️ Removing contaminated item: ${item.sku} @ £${(item.price / 100).toFixed(2)}`, {
            key: item.key,
            variant_id: item.variant_id,
            sku: item.sku,
            price: item.price
          });

          const removeResponse = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: item.key,
              quantity: 0
            })
          });

          if (removeResponse.ok) {
            this.debugLog(`✅ Removed contaminated item ${item.sku}`);
            this.logTelemetryEvent('cart_contamination_cleanup', {
              variantId: item.variant_id.toString(),
              sku: item.sku,
              price: item.price / 100,
              removed: true
            });
          } else {
            this.debugLog(`❌ Failed to remove contaminated item ${item.sku}`);
          }

          // Small delay between removals
          await new Promise(resolve => setTimeout(resolve, 100));

        } catch (err) {
          this.debugLog(`❌ Error removing contaminated item:`, err);
        }
      }

      this.debugLog(`✅ Cart contamination cleanup complete. Removed ${contaminatedItems.length} items.`);

      // Show a brief notification if items were removed (optional - may want to add UI feedback)
      if (contaminatedItems.length > 0) {
        console.warn(`⚠️ Removed ${contaminatedItems.length} invalid items from your cart. Your sizes were saved - see recovery banner after reload.`);
      }

      return contaminatedItems.length;

    } catch (error) {
      this.debugLog('❌ Cart contamination cleanup error:', error);
      return 0;
    }
  }

  // ==================== EXPIRED-CART RECOVERY ====================
  // Multi-day carts die when the shared pool variant is reset (3AM cleanup) or
  // reused by another buyer. Instead of silent removal, preserve the sizing
  // spec in localStorage so the customer gets 1-click restore, not square one.
  // Recovery keys (shared with calculator logic files):
  //   sgr_expired_recovery_v1 - array of snapshots {id, variantId, sku, price, quantity, properties, productType, timestamp, restored, dismissed}
  //   sgr_restore_request_v1  - single snapshot the calculator should prefill
  getRecoveryStore() {
    try {
      return JSON.parse(localStorage.getItem('sgr_expired_recovery_v1') || '[]');
    } catch (e) { return []; }
  }

  setRecoveryStore(list) {
    try {
      localStorage.setItem('sgr_expired_recovery_v1', JSON.stringify((list || []).slice(0, 5)));
    } catch (e) { /* storage full/blocked - non-critical */ }
  }

  detectProductTypeForVariant(variantIdStr) {
    if ((this.brickPoolVariants || []).includes(variantIdStr)) return 'brick';
    return 'glass';
  }

  saveExpiredRecovery(contaminatedItems) {
    try {
      const store = this.getRecoveryStore();
      const now = Date.now();
      for (const item of contaminatedItems) {
        const props = item.properties || {};
        const qty = parseInt(props['_Quantity'] || props['Quantity'] || item.quantity || 1, 10) || 1;
        const snapshot = {
          id: `${now}-${item.variant_id}`,
          variantId: item.variant_id.toString(),
          sku: item.sku || '',
          price: item.price / 100,
          quantity: qty,
          properties: props,
          productType: this.detectProductTypeForVariant(item.variant_id.toString()),
          timestamp: now,
          restored: false,
          dismissed: false
        };
        // Avoid duplicate snapshots for the same variant+spec within 1 hour
        const dupe = store.some(s => s.variantId === snapshot.variantId &&
          JSON.stringify(s.properties) === JSON.stringify(snapshot.properties) &&
          (now - s.timestamp) < 3600000);
        if (!dupe) store.unshift(snapshot);
        this.logTelemetryEvent('expired_recovery_saved', {
          variantId: snapshot.variantId,
          productType: snapshot.productType,
          quantity: qty
        });
      }
      this.setRecoveryStore(store);
    } catch (e) {
      this.debugLog('⚠️ Failed to save expired recovery:', e);
    }
  }

  summarizeRecoverySnapshot(snap) {
    try {
      const p = snap.properties || {};
      const pick = (keys) => {
        for (const k of keys) {
          if (p[k] !== undefined && p[k] !== null && String(p[k]).trim() !== '') return String(p[k]);
        }
        const found = Object.keys(p).find(k => keys.some(w => k.toLowerCase().includes(w.toLowerCase())));
        return found ? String(p[found]) : '';
      };
      const glassType = p['Glass Type'] || p['Brick Type'] || (snap.productType === 'brick' ? 'Custom brick' : 'Custom glass');
      const w = pick(['Overall Width', 'Width', 'Bottom Width', 'Diameter']);
      const h = pick(['Overall Height', 'Height', 'Bottom Width']);
      const dims = [w, h].filter(Boolean).join(' × ') || 'saved sizes';
      const qty = snap.quantity || 1;
      return `${glassType} — ${dims} (Qty: ${qty})`;
    } catch (e) { return 'your saved custom sizes'; }
  }

  renderExpiredRecoveryBanner() {
    try {
      if (document.getElementById('sgr-recovery-banner')) return;
      const pending = this.getRecoveryStore().filter(s => !s.restored && !s.dismissed && (Date.now() - s.timestamp) < 7 * 24 * 3600 * 1000);
      if (pending.length === 0) return;
      const banner = document.createElement('div');
      banner.id = 'sgr-recovery-banner';
      banner.setAttribute('role', 'alert');
      banner.style.cssText = 'background:#fff8e1;border:2px solid #f0ad4e;border-radius:8px;padding:14px 16px;margin:12px 0;font-size:14px;line-height:1.5;color:#333;';
      const itemsHtml = pending.map(s => {
        const summary = this.summarizeRecoverySnapshot(s);
        return `<li style="margin:6px 0;"><strong>${summary}</strong><br>` +
          `<button data-sgr-restore="${s.id}" style="margin:6px 8px 0 0;padding:8px 14px;background:#007bff;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Restore my sizes</button>` +
          `<button data-sgr-dismiss="${s.id}" style="margin:6px 0 0 0;padding:8px 14px;background:#eee;color:#333;border:none;border-radius:4px;cursor:pointer;font-size:13px;">Dismiss</button></li>`;
      }).join('');
      banner.innerHTML = `<strong>⚠️ Your custom item expired (shared price pool resets overnight).</strong><br>` +
        `Your sizes were saved — restore in 1 click, no retyping:<ul style="margin:8px 0 0 18px;padding:0;">${itemsHtml}</ul>`;
      const anchor = document.querySelector('.cart-container, main, [role="main"], body');
      if (anchor) anchor.insertBefore(banner, anchor.firstChild);
      banner.addEventListener('click', (e) => {
        const restoreBtn = e.target.closest('[data-sgr-restore]');
        const dismissBtn = e.target.closest('[data-sgr-dismiss]');
        if (restoreBtn) {
          const id = restoreBtn.getAttribute('data-sgr-restore');
          const store = this.getRecoveryStore();
          const snap = store.find(s => s.id === id);
          if (snap) {
            try { localStorage.setItem('sgr_restore_request_v1', JSON.stringify(snap)); } catch (err) {}
            snap.restored = true;
            this.setRecoveryStore(store);
            this.logTelemetryEvent('expired_recovery_restore_clicked', { variantId: snap.variantId });
            const url = snap.productType === 'brick'
              ? '/products/replacement-stove-bricks-cut-to-size-vermiculite-15mm-20mm-and-25mm-thick'
              : '/products/cut-to-size-stove-glass';
            window.location.href = url;
          }
        } else if (dismissBtn) {
          const id = dismissBtn.getAttribute('data-sgr-dismiss');
          const store = this.getRecoveryStore();
          const snap = store.find(s => s.id === id);
          if (snap) { snap.dismissed = true; this.setRecoveryStore(store); }
          banner.remove();
        }
      });
    } catch (e) {
      this.debugLog('⚠️ Failed to render recovery banner:', e);
    }
  }

  // Abandoned Cart Cleanup - Reset variants stuck in non-clean states
  async cleanupAbandonedVariants() {
    try {
      this.debugLog('🧹 Starting abandoned cart cleanup...');

      const ABANDON_TIMEOUT = 15 * 60 * 1000; // 15 minutes
      const now = Date.now();
      let cleanedCount = 0;

      for (const variantId of this.poolVariants) {
        const { state, timestamp } = this.getVariantStateWithTimestamp(variantId);

        // Skip variants that are already clean
        if (state === this.STATES.CLEAN) continue;

        // Check if variant has been stuck for too long
        const stuckTime = now - timestamp;

        if (stuckTime > ABANDON_TIMEOUT) {
          this.debugLog(`🚨 Found abandoned variant ${variantId}, stuck for ${Math.round(stuckTime / 1000 / 60)} minutes`);

          try {
            // Reset to clean state
            await this.resetVariant(variantId);
            cleanedCount++;

            // Small delay between resets to avoid rate limiting
            await new Promise(resolve => setTimeout(resolve, 100));

          } catch (error) {
            this.debugLog(`❌ Failed to cleanup variant ${variantId}:`, error);
          }
        }
      }

      this.debugLog(`✅ Abandoned cart cleanup complete. Cleaned ${cleanedCount} variants.`);

    } catch (error) {
      this.debugLog('❌ Abandoned cart cleanup error:', error);
    }
  }

  // Manual cleanup trigger for testing and emergency situations
  async forceCleanupAllVariants() {
    this.debugLog('🚨 FORCE CLEANUP: Resetting ALL variants regardless of state...');

    let cleanedCount = 0;
    let errorCount = 0;

    for (const variantId of this.poolVariants) {
      try {
        this.debugLog(`🔄 Force resetting variant ${variantId}...`);
        await this.resetVariant(variantId);
        cleanedCount++;

        // Clear local state regardless of server success
        localStorage.removeItem(`variant_state_${variantId}`);
        localStorage.removeItem(`variant_timestamp_${variantId}`);

        // Delay between resets to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 200));

      } catch (error) {
        errorCount++;
        this.debugLog(`❌ Failed to force reset variant ${variantId}:`, error);

        // Clear local state even if server reset failed
        localStorage.removeItem(`variant_state_${variantId}`);
        localStorage.removeItem(`variant_timestamp_${variantId}`);
      }
    }

    this.debugLog(`✅ Force cleanup complete. Reset ${cleanedCount} variants, ${errorCount} errors.`);
    this.debugLog('🧹 All local variant states cleared.');
    return { cleaned: cleanedCount, errors: errorCount };
  }

  // Ghost Data Detection - Catches abandoned cart data inheritance
  async detectGhostData(variantId, expectedPrice, quantity, cleanProperties, productType, fullCalculatorData = null) {
    try {
      this.debugLog('👻 Starting ghost data detection...');
      this.logTelemetryEvent('ghost_detection_start', {
        variantId: variantId,
        expectedPrice: expectedPrice,
        productType: productType
      });

      // POLLING STRATEGY: Check twice to handle slow Shopify API updates
      // First check: 300ms (fast path)
      // Second check: 800ms (slow path - if first check fails or is inconclusive)

      const checkCart = async (attemptName) => {
        if (this.debug) this.debugLog(`👻 Ghost detection check: ${attemptName}`);
        return await this.getThrottledCart();
      };

      // Wait for initial propagation
      await new Promise(resolve => setTimeout(resolve, 300));

      let cart = await checkCart('initial_300ms');

      // Handle Shopify discount splits - item may be split into multiple line items
      // Sum up total quantity for this variant across all cart items
      let addedItems = cart.items.filter(item => item.variant_id.toString() === variantId.toString());
      let totalQuantityInCart = addedItems.reduce((sum, item) => sum + item.quantity, 0);
      let addedItem = addedItems[0]; // Use first item for price/SKU check

      // If item not found, wait longer and try again (Shopify might be slow)
      if (!addedItem) {
        this.debugLog('⚠️ Item not found in cart on first check, waiting longer...');
        await new Promise(resolve => setTimeout(resolve, 500)); // Wait another 500ms (total 800ms)

        cart = await checkCart('retry_800ms');
        addedItems = cart.items.filter(item => item.variant_id.toString() === variantId.toString());
        totalQuantityInCart = addedItems.reduce((sum, item) => sum + item.quantity, 0);
        addedItem = addedItems[0];
      }


      if (!addedItem) {
        this.debugLog('⚠️ Item not found in cart for ghost detection');
        return;
      }

      this.debugLog('🔍 Checking for ghost data...', {
        expectedPrice: expectedPrice,
        actualPrice: addedItem.price,
        actualPriceInPounds: (addedItem.price / 100),
        actualSKU: addedItem.sku,
        variantId: variantId,
        fullCalculatorData: fullCalculatorData
      });

      // Check for price mismatch (ghost data from abandoned cart)
      const actualPriceInPounds = addedItem.price / 100;
      const priceDifference = expectedPrice ? Math.abs(actualPriceInPounds - expectedPrice) : 0;
      const priceMismatch = expectedPrice && priceDifference > 0.01;

      this.debugLog('💰 Price comparison:', {
        expectedPrice: expectedPrice,
        actualPriceInPounds: actualPriceInPounds,
        priceDifference: priceDifference,
        priceMismatch: priceMismatch
      });

      // Check for suspicious SKU patterns (ghost data indicators)
      // Only flag as suspicious if it doesn't match what we just generated
      const poolSKUPattern = /^POOL-\d{3}$/; // Expected clean pool SKU
      const suspiciousKU = false; // Disable pattern detection since exact SKU comparison is more reliable

      // Exact SKU verification - catches ALL ghost data scenarios
      const expectedSKU = fullCalculatorData ? this.generateSKU(fullCalculatorData) : null;

      // Defensive: if SKU generation failed, skip SKU mismatch check
      const skuMismatch = expectedSKU && addedItem.sku !== expectedSKU;

      // Debug shaped glass SKU generation
      if (fullCalculatorData && fullCalculatorData.shape !== 'square-rectangular') {
        this.debugLog('🔧 Shaped glass debug:', {
          shape: fullCalculatorData.shape,
          fullCalculatorData: fullCalculatorData,
          expectedSKU: expectedSKU,
          actualSKU: addedItem.sku,
          skuMismatch: skuMismatch
        });
      }

      this.debugLog('🏷️ SKU comparison:', {
        expectedSKU: expectedSKU,
        actualSKU: addedItem.sku,
        skuMismatch: skuMismatch,
        fullCalculatorDataExists: !!fullCalculatorData,
        generateSKUInput: fullCalculatorData
      });

      this.debugLog('🔍 Ghost detection summary:', {
        priceMismatch: priceMismatch,
        suspiciousKU: suspiciousKU,
        skuMismatch: skuMismatch,
        willTriggerGhostDetection: !!(priceMismatch || suspiciousKU || skuMismatch)
      });

      if (priceMismatch || suspiciousKU || skuMismatch) {
        this.debugLog('🚨 GHOST DATA DETECTED!', {
          priceMismatch: priceMismatch,
          suspiciousKU: suspiciousKU,
          skuMismatch: skuMismatch,
          expectedSKU: expectedSKU,
          actualSKU: addedItem.sku,
          action: 'Removing contaminated item and retrying with fresh variant'
        });

        // Track ghost detection in telemetry
        this.incrementCounter('ghostDetections', {
          variantId: variantId,
          reasons: {
            priceMismatch: priceMismatch,
            suspiciousKU: suspiciousKU,
            skuMismatch: skuMismatch
          },
          actualPrice: actualPriceInPounds,
          expectedPrice: expectedPrice,
          actualSKU: addedItem.sku,
          expectedSKU: expectedSKU
        });

        // Remove contaminated item from cart
        this.debugLog('🗑️ Removing contaminated item:', { id: addedItem.id, key: addedItem.key });

        const removeResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: addedItem.key, // Use key instead of id for cart removal
            quantity: 0
          })
        });

        if (!removeResponse.ok) {
          const errorText = await removeResponse.text();
          this.debugLog('❌ Failed to remove contaminated item:', {
            status: removeResponse.status,
            error: errorText,
            itemData: addedItem
          });
          // Continue anyway - better to have 2 items than infinite loop
        } else {
          this.debugLog('✅ Successfully removed contaminated item');
          this.incrementCounter('ghostRecoveries', {
            variantId: variantId,
            recoveryMethod: 'cart_item_removal'
          });
        }

        // Quarantine the contaminated variant
        this.setVariantState(variantId, this.STATES.QUARANTINED);

        // Automatically retry with a fresh variant
        this.debugLog('🔄 Auto-retrying with fresh variant...');

        // Find a different clean variant
        const freshVariantId = await this.findAvailableVariant();

        if (freshVariantId && freshVariantId !== variantId) {
          // Update the fresh variant with correct details (use fullCalculatorData, not cleanProperties)
          const title = productType === 'brick' ? 'Custom Cut Brick' : 'Custom Cut Glass';
          const sku = this.generateSKU(fullCalculatorData);

          // Don't update if SKU generation failed - keep the variant clean
          if (sku === null) {
            this.debugLog('⚠️ SKU generation failed for fresh variant, keeping default SKU');
            await this.updateVariantDetails(freshVariantId, {
              title: title,
              price: expectedPrice
              // Skip SKU update to keep clean POOL-XXX SKU
            });
          } else {
            await this.updateVariantDetails(freshVariantId, {
              title: title,
              price: expectedPrice,
              sku: sku
            });
          }

          // Add fresh variant to cart (mark as recovery to prevent infinite recursion)
          await new Promise(resolve => setTimeout(resolve, 200)); // Brief delay
          await this.addToCart(freshVariantId, quantity, cleanProperties, productType, expectedPrice, true, fullCalculatorData);

          this.debugLog('✅ Successfully recovered from ghost data with fresh variant');
        } else {
          this.debugLog('⚠️ No fresh variant available, falling back to draft orders');
          throw new Error('Ghost data detected but no clean variants available');
        }
      } else {
        this.debugLog('✅ No ghost data detected - item is clean');
      }

    } catch (error) {
      this.debugLog('❌ Ghost data detection error:', error);
      // Don't throw - let the order proceed even if detection fails
    }
  }

  // Reset variant to clean default state
  async resetVariant(variantId, productType = 'glass') {
    try {
      if (this.debug) this.debugLog(`🔄 Resetting variant ${variantId} to clean state...`);

      this.setVariantState(variantId, this.STATES.PENDING_CLEANUP);

      const defaultState = this.getDefaultVariantState(variantId, productType);

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 12000); // 12 second timeout - increased for resilience

      const response = await fetch(this.updateEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          operation: 'reset',
          productType: productType,
          variant: {
            id: variantId
          }
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Reset failed: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Reset failed');
      }

      // Validate server response for pool variants
      if (this.debug) {
        this.debugLog('📋 Reset server response:', result);
        this.debugLog('✅ Variant reset completed successfully - server confirmed');
      }

      this.setVariantState(variantId, this.STATES.CLEAN);
      if (this.debug) this.debugLog(`✅ Variant ${variantId} reset and validated successfully`);

      // DEFENSIVE LAYER 1: Track reset timestamp to avoid using variant too soon after reset
      this.recentlyResetVariants.set(variantId, Date.now());
      if (this.debug) {
        this.debugLog(`🛡️ Marked variant ${variantId} as recently reset (will skip for ${this.MIN_VARIANT_AGE_MS / 1000}s)`);
      }

      // Track successful variant reset
      this.incrementCounter('variantResets', {
        variantId: variantId,
        productType: productType,
        resetMethod: 'server_endpoint'
      });
      this.logTelemetryEvent('variant_reset_success', {
        variantId: variantId,
        productType: productType,
        resetDuration: Date.now() - this.telemetry.startTime
      });

      return true;


    } catch (error) {
      this.debugLog(`❌ Failed to reset variant ${variantId}:`, error);
      this.setVariantState(variantId, this.STATES.QUARANTINED);

      // Alert admin about quarantined variant
      this.alertQuarantinedVariant(variantId, error.message);

      return false;
    }
  }

  // Validate that variant is actually in expected state
  async validateVariantState(variantId, expectedState) {
    try {
      if (this.debug) this.debugLog(`🔍 Validating variant ${variantId} state...`);

      // For pool variants, rely on the success of the update operation rather than 
      // trying to find them in public product listings
      if (this.poolVariants.includes(variantId)) {
        // Since we're using the variant pool system, we can trust that if the 
        // update operation succeeded, the variant is in the correct state
        if (this.debug) {
          this.debugLog(`✅ Pool variant ${variantId} validation assumed successful (pool system)`);
          this.debugLog(`📝 Expected state:`, expectedState);
        }
        return true;
      }

      // For non-pool variants, use the original validation approach
      const response = await fetch(`/products.json`);
      const data = await response.json();

      // Find the variant across all products
      let actualVariant = null;
      for (const product of data.products) {
        actualVariant = product.variants.find(v => v.id.toString() === variantId);
        if (actualVariant) break;
      }

      if (!actualVariant) {
        if (this.debug) this.debugLog(`❌ Variant ${variantId} not found in public products`);
        return false;
      }

      // Check if variant matches expected state
      const priceMatch = Math.abs(parseFloat(actualVariant.price) - parseFloat(expectedState.price)) < 0.01;
      const titleMatch = actualVariant.title === expectedState.title;
      const skuMatch = actualVariant.sku === expectedState.sku;

      const isValid = priceMatch && titleMatch && skuMatch;

      if (this.debug) {
        this.debugLog(`🔍 Validation results for ${variantId}:`, {
          priceMatch: `${actualVariant.price} === ${expectedState.price} = ${priceMatch}`,
          titleMatch: `"${actualVariant.title}" === "${expectedState.title}" = ${titleMatch}`,
          skuMatch: `"${actualVariant.sku}" === "${expectedState.sku}" = ${skuMatch}`,
          overall: isValid
        });
      }

      return isValid;

    } catch (error) {
      this.debugLog(`❌ Error validating variant ${variantId}:`, error);
      return false;
    }
  }

  // Quarantine variant and alert admin
  alertQuarantinedVariant(variantId, errorMessage) {
    const alertData = {
      variantId: variantId,
      timestamp: new Date().toISOString(),
      error: errorMessage,
      poolType: this.poolVariants === this.glassPoolVariants ? 'glass' : 'brick'
    };

    // Store quarantine info
    const quarantineKey = `quarantined_${variantId}`;
    localStorage.setItem(quarantineKey, JSON.stringify(alertData));

    // Log for admin monitoring
    this.debugLog('🚨 VARIANT QUARANTINED - ADMIN ATTENTION REQUIRED:', alertData);

    // Could also send to external monitoring service here
    // this.sendQuarantineAlert(alertData);
  }

  // Enhanced findAvailableVariant - only returns CLEAN variants
  // Throttled cart fetcher to prevent redundant API calls
  async getThrottledCart() {
    const now = Date.now();

    // Check if we have a valid cache
    if (this.cartCache.data && (now - this.cartCache.timestamp < this.cartCache.TTL)) {
      if (this.debug) this.debugLog('📦 Using cached cart data');
      return this.cartCache.data;
    }

    // Check if a fetch is already in progress
    if (this.cartCache.promise) {
      if (this.debug) this.debugLog('🕒 Waiting for existing cart fetch promise');
      return this.cartCache.promise;
    }

    // Otherwise, fetch fresh data
    if (this.debug) this.debugLog('🌐 Fetching fresh cart data (throttle refresh)');
    this.cartCache.promise = fetch('/cart.js')
      .then(response => {
        if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
        return response.json();
      })
      .then(cart => {
        this.cartCache.data = cart;
        this.cartCache.timestamp = Date.now();
        this.cartCache.promise = null;
        return cart;
      })
      .catch(error => {
        this.cartCache.promise = null;
        throw error;
      });

    return this.cartCache.promise;
  }

  async findAvailableVariant() {
    try {
      if (this.debug) this.debugLog('🔍 Finding available CLEAN variant...');

      // Get current cart to see which variants are in use
      const cart = await this.getThrottledCart();

      const variantsInCart = cart.items
        .filter(item => this.poolVariants.includes(item.variant_id.toString()))
        .map(item => item.variant_id.toString());

      if (this.debug) {
        this.debugLog('🛒 Variants in cart:', variantsInCart);
      }

      // DEFENSIVE LAYER 1: Clean up old reset timestamps (older than 5 minutes)
      const now = Date.now();
      for (const [variantId, resetTime] of this.recentlyResetVariants.entries()) {
        if (now - resetTime > 300000) { // 5 minutes
          this.recentlyResetVariants.delete(variantId);
        }
      }

      // Get recently updated variants to avoid
      const recentlyUpdated = JSON.parse(localStorage.getItem('recentUpdates') || '{}');

      // Clean up old entries (older than 60 seconds)
      const cleanRecentUpdates = {};
      Object.keys(recentlyUpdated).forEach(vId => {
        if ((now - recentlyUpdated[vId]) < 60000) {
          cleanRecentUpdates[vId] = recentlyUpdated[vId];
        }
      });
      localStorage.setItem('recentUpdates', JSON.stringify(cleanRecentUpdates));

      // DEFENSIVE LAYER 1: Get list of recently reset variants to avoid
      const recentlyResetIds = [];
      for (const [variantId, resetTime] of this.recentlyResetVariants.entries()) {
        const variantAge = now - resetTime;
        if (variantAge < this.MIN_VARIANT_AGE_MS) {
          recentlyResetIds.push(variantId);
          if (this.debug) {
            this.debugLog(`🛡️ Variant ${variantId} too fresh after reset (${Math.round(variantAge / 1000)}s old, need ${this.MIN_VARIANT_AGE_MS / 1000}s)`);
          }
        }
      }

      // Find variants that are CLEAN, not in cart, and not recently updated/reset
      const cleanVariants = [];

      for (const variantId of this.poolVariants) {
        // Skip if in cart
        if (variantsInCart.includes(variantId)) {
          continue;
        }

        // Skip if recently updated (within 60 seconds)
        if (cleanRecentUpdates[variantId]) {
          if (this.debug) {
            this.debugLog(`Skipping recently updated variant ${variantId} (${Math.round((now - cleanRecentUpdates[variantId]) / 1000)}s ago)`);
          }
          continue;
        }

        // DEFENSIVE LAYER 1: Skip if recently reset (CDN propagation safety)
        if (recentlyResetIds.includes(variantId)) {
          if (this.debug) {
            this.debugLog(`⏭️ Skipping variant ${variantId} (recently reset, awaiting CDN propagation)`);
          }
          continue;
        }

        // DEFENSIVE LAYER 2: Skip if currently being processed (duplicate pool prevention)
        if (this.pendingVariants.has(variantId)) {
          if (this.debug) {
            this.debugLog(`⏭️ Skipping variant ${variantId} (currently being processed by another operation)`);
          }
          continue;
        }

        // Check state
        const state = this.getVariantState(variantId);

        if (state === this.STATES.CLEAN) {
          cleanVariants.push(variantId);
        } else if (state === this.STATES.PENDING_CLEANUP) {
          // Skip variants being cleaned up
          continue;
        } else if (state === this.STATES.QUARANTINED) {
          // Skip quarantined variants
          continue;
        }
      }

      if (this.debug) {
        this.debugLog('✅ Clean variants available:', cleanVariants.length);
      }

      if (cleanVariants.length === 0) {
        // Try to clean up some variants if none are available
        await this.emergencyCleanup();

        // Check again after cleanup
        const retryCleanVariants = this.poolVariants.filter(variantId =>
          !variantsInCart.includes(variantId) &&
          this.getVariantState(variantId) === this.STATES.CLEAN
        );

        if (retryCleanVariants.length === 0) {
          throw new Error('No clean variants available after emergency cleanup');
        }

        // Apply round-robin selection even after emergency cleanup
        const poolType = this.poolVariants === this.glassPoolVariants ? 'glass' : 'brick';
        const storageKey = `lastPoolIndex_${poolType}`;
        const lastUsedIndex = parseInt(localStorage.getItem(storageKey) || '0');
        // Seed to reduce first-visit bias
        let seed = parseInt(localStorage.getItem('poolVisitorSeed') || 'NaN', 10);
        if (isNaN(seed)) { seed = Math.floor(Math.random() * 7); localStorage.setItem('poolVisitorSeed', String(seed)); }
        const nextIndex = (lastUsedIndex + 1 + seed) % retryCleanVariants.length;
        localStorage.setItem(storageKey, nextIndex.toString());

        if (this.debug) {
          this.debugLog(`🚨 Emergency cleanup round-robin: using index ${nextIndex} of ${retryCleanVariants.length} variants`);
        }

        const selectedVariant = retryCleanVariants[nextIndex];
        // CRITICAL: Atomic Reservation
        this.pendingVariants.add(selectedVariant);
        return selectedVariant;
      }

      // Round-robin selection to distribute load across all pools
      const poolType = this.poolVariants === this.glassPoolVariants ? 'glass' : 'brick';
      const storageKey = `lastPoolIndex_${poolType}`;
      const lastUsedIndex = parseInt(localStorage.getItem(storageKey) || '0');
      // Seed to reduce first-visit bias
      let seed = parseInt(localStorage.getItem('poolVisitorSeed') || 'NaN', 10);
      if (isNaN(seed)) { seed = Math.floor(Math.random() * 7); localStorage.setItem('poolVisitorSeed', String(seed)); }
      const nextIndex = (lastUsedIndex + 1 + seed) % cleanVariants.length;
      localStorage.setItem(storageKey, nextIndex.toString());

      const selectedVariantId = cleanVariants[nextIndex];

      // CRITICAL SAFETY: Mark as pending IMMEDIATELY (Atomic Reservation)
      // This prevents race conditions where simultaneous clicks pick the same "CLEAN" variant
      this.pendingVariants.add(selectedVariantId);

      if (this.debug) {
        this.debugLog(`🎯 Selection: Using variant ${selectedVariantId} (Pool Index ${nextIndex})`);
      }

      return selectedVariantId;
    } catch (error) {
      this.debugLog('❌ Error finding available variant:', error);
      throw error;
    }
  }

  // Emergency cleanup - try to reset some variants
  async emergencyCleanup() {
    if (this.debug) this.debugLog('🚨 Emergency cleanup initiated...');

    // Try to clean variants starting from different positions for better distribution
    const startIndex = Math.floor(Math.random() * this.poolVariants.length);
    const variantsToClean = [];

    // Select 10 variants randomly distributed across the pool
    for (let i = 0; i < 10 && i < this.poolVariants.length; i++) {
      const index = (startIndex + i * 5) % this.poolVariants.length;
      variantsToClean.push(this.poolVariants[index]);
    }

    for (const variantId of variantsToClean) {
      const state = this.getVariantState(variantId);

      if (state !== this.STATES.QUARANTINED) {
        try {
          await this.resetVariant(variantId);
        } catch (error) {
          // Continue with next variant if this one fails
          continue;
        }
      }
    }
  }

  // Set product type and switch pools
  setProductType(productType = 'glass') {
    this.productType = productType;
    if (productType === 'brick') {
      this.poolVariants = this.brickPoolVariants;
      if (this.debug) this.debugLog('🧱 Using brick pool variants');
    } else {
      this.poolVariants = this.glassPoolVariants;
      if (this.debug) this.debugLog('🔍 Using glass pool variants');
    }
  }

  // Filter properties for clean cart display and supplier-friendly order emails
  filterCartProperties(properties, productType = 'glass') {
    const cleanProperties = {};
    const accessories = [];

    // Define property display order for cleaner supplier emails
    const priorityOrder = ['Glass Type', 'Brick Type', 'Shape Details', 'Glass Shape', 'Brick Shape',
      'Overall Width', 'Overall Height', 'Width', 'Height', 'Diameter',
      'Height to Cut-off', 'Top Flat Width', 'Corner Radius', 'Thickness', 'Drawing Uploaded'];
    const accessoryKeys = ['Door Rope Kit', 'Glass Tape', 'Gasket'];
    const skipKeys = ['Measurements In', 'Dimensions', 'Area per Unit', 'Total Area', 'Shape', 'Accessory Qty', 'Accessory Choice', 'Drawing Uploaded'];

    // First pass: collect accessories separately
    Object.keys(properties).forEach(key => {
      if (accessoryKeys.includes(key) && properties[key]) {
        accessories.push(properties[key]);
      }
    });

    // Second pass: add properties in priority order
    priorityOrder.forEach(key => {
      if (properties[key] && !accessoryKeys.includes(key)) {
        cleanProperties[key] = properties[key];
      }
    });

    // Third pass: add remaining properties (except skipped ones, internal ones, and accessories)
    Object.keys(properties).forEach(key => {
      // Native Shopify hiding: always allow internal system properties
      if (key === '_View Drawing' || key === '_Spec Image' || key === '_Drawing' ||
        key === '_Expected Price' || key === '_Expected SKU' || key === '_Product Title' ||
        key === '_ClientPatchVersion' || key === '_ValidationSource' || key === '_AddPath') {
        cleanProperties[key] = properties[key];
        return;
      }
      if (key.startsWith('_')) return;
      if (skipKeys.includes(key)) return;
      if (accessoryKeys.includes(key)) return;
      if (priorityOrder.includes(key)) return; // Already added

      // Handle Shape conversion for backward compatibility
      if (key === 'Shape') {
        if (productType === 'brick') {
          const brickShapeNames = {
            'square-rectangular': 'Rectangular',
            'shaped': 'Shaped'
          };
          if (!cleanProperties['Brick Shape']) {
            cleanProperties['Brick Shape'] = brickShapeNames[properties[key]] || properties[key];
          }
        } else {
          const glassShapeNames = {
            'square-rectangular': 'Rectangular',
            'arched': 'Arched',
            'half-arch': 'Half Arch',
            'both-top-corners-cut': 'Corner Cut',
            'single-corner-cut': 'Single Corner Cut',
            'all-corners-cut': 'All Corners Cut',
            'sloped-top': 'Sloped Top',
            'circular': 'Circular'
          };
          if (!cleanProperties['Glass Shape']) {
            cleanProperties['Glass Shape'] = glassShapeNames[properties[key]] || properties[key];
          }
        }
        return;
      }

      cleanProperties[key] = properties[key];
    });

    // Add combined accessories at the end (if any)
    if (accessories.length > 0) {
      let kitsCount = 1;
      let isFixedTotal = false;

      // Extract quantity details
      if (properties['Accessory Qty']) {
        const val = properties['Accessory Qty'];
        if (val.includes('Fixed Total')) {
          const match = val.match(/^(\d+)/);
          if (match) {
            kitsCount = parseInt(match[1], 10);
            isFixedTotal = true;
          }
        }
      }

      // Format: "1 x Item (Per piece)" or "3 x Item (Order total)"
      const formattedAccessories = accessories.map(a => {
        const suffix = isFixedTotal ? 'Order total' : 'Per piece';
        return `${kitsCount} x ${a} (${suffix})`;
      });

      cleanProperties['Accessories'] = formattedAccessories.join(', ');
    }

    return cleanProperties;
  }

  // Get dynamic delay based on recent error history
  getDynamicDelay(productType) {
    const errorHistoryKey = `priceErrors_${productType}`;
    const errorHistory = JSON.parse(localStorage.getItem(errorHistoryKey) || '[]');
    const now = Date.now();

    // Clean up old errors (older than 15 minutes)
    const recentErrors = errorHistory.filter(timestamp => now - timestamp < 15 * 60 * 1000);
    localStorage.setItem(errorHistoryKey, JSON.stringify(recentErrors));

    // Determine delay based on recent error count
    if (recentErrors.length === 0) {
      return productType === 'brick' ? 400 : 300; // Fast when stable
    } else if (recentErrors.length <= 2) {
      return productType === 'brick' ? 700 : 500; // Medium when some issues
    } else {
      return productType === 'brick' ? 1200 : 800; // Conservative when problematic
    }
  }

  // Record price error for dynamic delay calculation
  recordPriceError(productType) {
    const errorHistoryKey = `priceErrors_${productType}`;
    const errorHistory = JSON.parse(localStorage.getItem(errorHistoryKey) || '[]');
    errorHistory.push(Date.now());
    localStorage.setItem(errorHistoryKey, JSON.stringify(errorHistory));
  }

  /**
   * Verify that the item just added to the cart has the correct price and SKU.
   * This catches Shopify propagation lag where the Storefront API serves a stale £0.01 price.
   * @param {string} variantId - The variant ID to check
   * @param {number} expectedPrice - The price we expected
   * @param {string|null} expectedSKU - The SKU we expected
   * @param {number} retryCount - Retry attempt for fresh-cart propagation delays
   * @returns {object} { success: boolean, actualPrice: number, lineItemIndex: number }
   */
  async verifyPriceInCart(variantId, expectedPrice, expectedSKU = null, retryCount = 0) {
    try {
      const MAX_RETRIES = 1;
      const RETRY_DELAY_MS = 700;

      // Verification must bypass the normal cart cache because this runs immediately after /cart/add.js.
      const response = await fetch(`/cart.js?_cb=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`Fresh cart fetch failed: ${response.status}`);
      }

      const cart = await response.json();
      this.cartCache.data = cart;
      this.cartCache.timestamp = Date.now();
      this.cartCache.promise = null;

      const expectedPriceFixed = parseFloat(expectedPrice.toFixed(2));
      const expectedSKUString = expectedSKU ? String(expectedSKU) : null;

      // Find the item we just added - try property matching first (most accurate)
      let matchedItem = cart.items.find(item => {
        if (item.variant_id.toString() !== variantId.toString()) return false;
        return item.properties && item.properties['_Expected Price'] === expectedPriceFixed.toFixed(2);
      });

      // Fallback: If property matching failed, find ANY item with this variant ID
      // This handles race conditions where property hasn't propagated yet
      if (!matchedItem) {
        const variantItems = cart.items.filter(item =>
          item.variant_id.toString() === variantId.toString()
        );
        // Take the last one (most recently added)
        if (variantItems.length > 0) {
          matchedItem = variantItems[0];
          console.log('🔎 Using fallback variant matching (Index 0 - Most Recent)');
        }
      }

      if (!matchedItem) {
        if (retryCount < MAX_RETRIES) {
          console.warn('⚠️ Item not found in fresh cart during verification - retrying once');
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
          return this.verifyPriceInCart(variantId, expectedPrice, expectedSKUString, retryCount + 1);
        }

        console.error('🚨 Item not found in fresh cart after add - failing closed');
        return {
          success: false,
          reason: 'item_not_found_after_add',
          actualPrice: null,
          actualSKU: null,
          lineItemIndex: null
        };
      }

      // Check the price. Line item price is in cents.
      const actualPrice = matchedItem.price / 100;
      const isPriceCorrect = Math.abs(actualPrice - expectedPriceFixed) < 0.01;
      const actualSKU = matchedItem.sku ? String(matchedItem.sku) : '';
      const isSKUCorrect = !expectedSKUString || actualSKU === expectedSKUString;

      if (!isPriceCorrect) {
        console.warn('🚨 PRICE MISMATCH DETECTED IN CART:', {
          variantId: variantId,
          expected: expectedPriceFixed,
          actual: actualPrice
        });
      }
      if (!isSKUCorrect) {
        console.warn('🚨 SKU MISMATCH DETECTED IN CART:', {
          variantId: variantId,
          expected: expectedSKUString,
          actual: actualSKU
        });
      }

      return {
        success: isPriceCorrect && isSKUCorrect,
        actualPrice: actualPrice,
        actualSKU: actualSKU,
        reason: isPriceCorrect && isSKUCorrect ? 'verified' : 'cart_item_mismatch',
        mismatch: {
          price: !isPriceCorrect,
          sku: !isSKUCorrect
        },
        lineItemIndex: cart.items.indexOf(matchedItem) + 1 // 1-based index
      };
    } catch (error) {
      console.error('Price verification error:', error);
      return { success: true, reason: 'verification_failed_fetch' }; // Fail open on network error
    }
  }

  /**
   * Remove a contaminated item from the cart
   * @param {number} lineIndex - 1-based index of the line item
   */
  async removeCartItem(lineIndex) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: lineIndex, quantity: 0 })
      });
      if (this.debug) this.debugLog(`🗑️ Removed contaminated item at line ${lineIndex}`);
    } catch (error) {
      console.error('Error removing cart item:', error);
    }
  }

  // Update variant details with retry logic, timeout, and dynamic delays
  async updateVariantDetails(variantId, details, retryCount = 0) {
    const maxRetries = 2;
    const timeoutMs = 12000; // 12 second timeout - increased for slow networks/cold starts

    try {
      if (this.debug) {
        this.debugLog(`📝 Updating variant: ${variantId} (attempt ${retryCount + 1})`);
        this.debugLog('📝 Details:', details);
      }

      const roundedPrice = Number(details.price).toFixed(2);
      const requestBody = {
        variant: {
          id: variantId,
          price: roundedPrice,
          sku: details.sku
        }
      };

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      const response = await fetch(this.updateEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update variant: ${response.status} - ${errorText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Update failed');
      }

      // Additional validation: Wait and verify the price update propagated correctly
      // This is critical for brick calculator to avoid wrong prices in cart
      if (this.debug) {
        this.debugLog('⏳ Waiting for price propagation...');
      }

      // Use dynamic delay based on recent error history
      const productType = this.poolVariants === this.glassPoolVariants ? 'glass' : 'brick';
      const dynamicDelay = this.getDynamicDelay(productType);

      if (this.debug) {
        this.debugLog(`⏳ Using dynamic delay: ${dynamicDelay}ms (based on recent error history)`);
      }

      await new Promise(resolve => setTimeout(resolve, dynamicDelay));

      // Mark variant as recently updated for 60-second avoidance window
      const recentlyUpdated = JSON.parse(localStorage.getItem('recentUpdates') || '{}');
      recentlyUpdated[variantId] = Date.now();
      localStorage.setItem('recentUpdates', JSON.stringify(recentlyUpdated));

      if (this.debug) {
        this.debugLog('✅ Variant update completed with propagation verification');
        this.debugLog(`📝 Marked variant ${variantId} as recently updated`);
      }

      return result;

    } catch (error) {
      // Handle timeout with one retry before failing - improves resilience for transient cold starts
      if (error.name === 'AbortError') {
        this.debugLog('❌ Netlify endpoint timeout - server may be down');
        if (retryCount < maxRetries) {
          this.debugLog(`⚠️ Timeout detected, retrying... (${retryCount + 1}/${maxRetries})`);
          const retryDelay = 1000;
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          return await this.updateVariantDetails(variantId, details, retryCount + 1);
        }
        throw new Error('NETLIFY_TIMEOUT');
      }

      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        this.debugLog('❌ Network error - Netlify may be unavailable');
        if (retryCount < maxRetries) {
          this.debugLog(`⚠️ Network error, retrying... (${retryCount + 1}/${maxRetries})`);
          const retryDelay = retryCount === 0 ? 500 : 1000;
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          return await this.updateVariantDetails(variantId, details, retryCount + 1);
        }
        throw new Error('NETLIFY_UNAVAILABLE');
      }

      if (retryCount < maxRetries) {
        this.debugLog(`⚠️ Update failed, retrying... (${retryCount + 1}/${maxRetries})`);
        const retryDelay = retryCount === 0 ? 300 : 700;
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return await this.updateVariantDetails(variantId, details, retryCount + 1);
      }

      this.debugLog('❌ Error updating variant after all retries:', error);
      throw error;
    }
  }

  /**
   * Verify variant before adding to cart by fetching fresh data from Shopify Storefront API
   * This catches cases where the variant was overwritten by another user/session
   * SMART FAIL-SAFE: Only blocks on CONFIRMED ghost data (SKU/price mismatch), not network issues
   * @param {string} variantId - The variant ID to verify
   * @param {string} productType - 'glass' or 'brick'
   * @param {string} expectedSKU - The SKU we expect to see
   * @param {number} expectedPrice - The price we expect to see
   * @param {number} retryCount - Retry attempt (internal use)
   * @returns {object} { success: boolean, actual: { sku, price }, mismatch: { sku, price } }
   */
  async verifyVariantBeforeCart(variantId, productType, expectedSKU, expectedPrice, retryCount = 0) {
    const MAX_RETRIES = 1; // One retry for transient issues
    const TIMEOUT_MS = 8000; // 8 seconds - realistic timeout
    // CDN propagation backoff delays (ms) - wait for Shopify CDN to catch up after Admin API update
    const PROPAGATION_DELAYS = [0, 800, 1800, 3500]; // Try immediately, then 0.8s, 1.8s, 3.5s
    const startTime = Date.now();

    try {
      // Determine product handle based on type
      const productHandle = productType === 'brick'
        ? 'replacement-stove-bricks-cut-to-size-vermiculite-15mm-20mm-and-25mm-thick'
        : 'cut-to-size-stove-glass';

      if (this.debug) {
        this.debugLog(`🔍 Fetching fresh variant from Shopify (attempt ${retryCount + 1}): /products/${productHandle}.js`);
      }

      // Create abort controller for timeout protection
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

      // Cache-busting: Add timestamp query param AND no-store header to bypass CDN/browser cache
      const cacheBuster = `_cb=${Date.now()}`;
      const fetchUrl = `/products/${productHandle}.js?${cacheBuster}`;

      // Fetch fresh product data from Shopify Storefront API with cache-busting
      const response = await fetch(fetchUrl, {
        signal: controller.signal,
        cache: 'no-store',  // Bypass browser cache
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      clearTimeout(timeoutId);

      const fetchDuration = Date.now() - startTime;

      if (!response.ok) {
        console.warn(`⚠️ Verification fetch failed (${response.status}) after ${fetchDuration}ms`);

        // Log telemetry
        this.logTelemetryEvent('verification_fetch_failed', {
          variantId: variantId,
          status: response.status,
          duration: fetchDuration,
          retryCount: retryCount
        });

        // Retry once for transient issues
        if (retryCount < MAX_RETRIES) {
          console.log('🔄 Retrying verification (network issue)...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          return this.verifyVariantBeforeCart(variantId, productType, expectedSKU, expectedPrice, retryCount + 1);
        }

        // After retry: USE FALLBACK (changed from fail-open to fail-to-fallback)
        console.warn('⚠️ Verification fetch failed - triggering penny fallback for safety');
        this.logTelemetryEvent('verification_fallback_triggered', {
          variantId: variantId,
          reason: 'fetch_failed_after_retry',
          retryCount: retryCount
        });
        return {
          success: 'FALLBACK_REQUIRED',  // Triggers penny-based fallback
          actual: { sku: 'fetch-failed', price: 'fetch-failed' },
          reason: 'Network issue - using penny fallback for correct pricing'
        };
      }

      const productData = await response.json();
      const actualVariant = productData.variants.find(v => v.id.toString() === variantId.toString());

      if (!actualVariant) {
        console.warn(`⚠️ Variant ${variantId} not found in product data after ${fetchDuration}ms`);

        // Log telemetry
        this.logTelemetryEvent('verification_variant_not_found', {
          variantId: variantId,
          duration: fetchDuration,
          retryCount: retryCount,
          totalVariantsInProduct: productData.variants.length
        });

        // Retry once
        if (retryCount < MAX_RETRIES) {
          console.log('🔄 Retrying verification (variant not found)...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          return this.verifyVariantBeforeCart(variantId, productType, expectedSKU, expectedPrice, retryCount + 1);
        }

        // After retry: USE FALLBACK (changed from fail-open to fail-to-fallback)
        console.warn('⚠️ Variant not found after retry - triggering penny fallback for safety');
        this.logTelemetryEvent('verification_fallback_triggered', {
          variantId: variantId,
          reason: 'variant_not_found_after_retry',
          retryCount: retryCount
        });
        return {
          success: 'FALLBACK_REQUIRED',  // Triggers penny-based fallback
          actual: { sku: 'not-found', price: 'not-found' },
          reason: 'Shopify API issue - using penny fallback for correct pricing'
        };
      }

      // WE HAVE ACTUAL VARIANT DATA - NOW CHECK FOR GHOST DATA
      const actualSKU = actualVariant.sku;
      const actualPrice = parseFloat(actualVariant.price) / 100; // Shopify returns price in cents

      const skuMatch = actualSKU === expectedSKU;
      const priceMatch = Math.abs(actualPrice - expectedPrice) < 0.01;

      if (this.debug || retryCount > 0) {
        console.log(`🔍 Verification complete (${fetchDuration}ms):`, {
          variantId: variantId,
          expected: { sku: expectedSKU, price: expectedPrice },
          actual: { sku: actualSKU, price: actualPrice },
          match: skuMatch && priceMatch
        });
      }

      if (skuMatch && priceMatch) {
        // VERIFIED CLEAN!
        if (retryCount > 0) {
          console.log('✅ Verification passed on retry');
        }

        this.logTelemetryEvent('verification_passed', {
          variantId: variantId,
          duration: fetchDuration,
          retryCount: retryCount
        });

        return {
          success: true,
          actual: { sku: actualSKU, price: actualPrice },
          mismatch: { sku: false, price: false }
        };
      } else if (priceMatch && !skuMatch) {
        // PRICE MATCHES BUT SKU IS STALE (CDN lag) - ALLOW ORDER
        // The actual order will have correct SKU from Shopify origin server
        console.warn('⚠️ SKU mismatch (CDN lag) but price matches - proceeding with order:', {
          variantId: variantId,
          expectedSKU: expectedSKU,
          actualSKU: actualSKU,
          price: actualPrice
        });

        this.logTelemetryEvent('verification_sku_lag', {
          variantId: variantId,
          expectedSKU: expectedSKU,
          actualSKU: actualSKU,
          price: actualPrice,
          duration: fetchDuration,
          retryCount: retryCount
        });

        return {
          success: true, // ALLOW ORDER - price is correct, SKU lag is acceptable
          actual: { sku: actualSKU, price: actualPrice },
          mismatch: { sku: true, price: false },
          note: 'SKU mismatch due to CDN lag - order proceeds with correct pricing'
        };
      } else {
        // PRICE MISMATCH DETECTED - But could be CDN propagation lag!
        // Try propagation backoff before declaring true mismatch
        console.warn('⚠️ Price mismatch detected - attempting CDN propagation backoff...', {
          variantId: variantId,
          expected: { sku: expectedSKU, price: expectedPrice },
          actual: { sku: actualSKU, price: actualPrice }
        });

        // Propagation backoff loop - give Shopify CDN time to catch up
        for (let backoffAttempt = 1; backoffAttempt < PROPAGATION_DELAYS.length; backoffAttempt++) {
          const delay = PROPAGATION_DELAYS[backoffAttempt];
          console.log(`⏳ CDN propagation backoff: waiting ${delay}ms (attempt ${backoffAttempt}/${PROPAGATION_DELAYS.length - 1})...`);

          await new Promise(resolve => setTimeout(resolve, delay));

          try {
            // Re-fetch with fresh cache-buster
            const backoffCacheBuster = `_cb=${Date.now()}`;
            const backoffUrl = `/products/${productHandle}.js?${backoffCacheBuster}`;
            const backoffResponse = await fetch(backoffUrl, {
              cache: 'no-store',
              headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache'
              }
            });

            if (backoffResponse.ok) {
              const backoffData = await backoffResponse.json();
              const backoffVariant = backoffData.variants.find(v => v.id.toString() === variantId.toString());

              if (backoffVariant) {
                const backoffSKU = backoffVariant.sku;
                const backoffPrice = parseFloat(backoffVariant.price) / 100;
                const backoffSkuMatch = backoffSKU === expectedSKU;
                const backoffPriceMatch = Math.abs(backoffPrice - expectedPrice) < 0.01;

                console.log(`🔍 Backoff attempt ${backoffAttempt} result:`, {
                  expected: { sku: expectedSKU, price: expectedPrice },
                  actual: { sku: backoffSKU, price: backoffPrice },
                  match: backoffSkuMatch && backoffPriceMatch
                });

                if (backoffSkuMatch && backoffPriceMatch) {
                  // CDN caught up! Verification passed after backoff
                  console.log(`✅ CDN propagation successful after ${delay}ms backoff`);
                  this.logTelemetryEvent('verification_passed_after_backoff', {
                    variantId: variantId,
                    backoffAttempt: backoffAttempt,
                    totalDelay: PROPAGATION_DELAYS.slice(0, backoffAttempt + 1).reduce((a, b) => a + b, 0),
                    duration: Date.now() - startTime
                  });
                  return {
                    success: true,
                    actual: { sku: backoffSKU, price: backoffPrice },
                    mismatch: { sku: false, price: false },
                    note: `CDN propagation succeeded after ${backoffAttempt} backoff attempts`
                  };
                } else if (backoffPriceMatch && !backoffSkuMatch) {
                  // Price now matches, SKU still lagging - that's acceptable
                  console.log(`✅ Price matches after backoff (SKU still lagging - acceptable)`);
                  this.logTelemetryEvent('verification_price_match_after_backoff', {
                    variantId: variantId,
                    backoffAttempt: backoffAttempt,
                    duration: Date.now() - startTime
                  });
                  return {
                    success: true,
                    actual: { sku: backoffSKU, price: backoffPrice },
                    mismatch: { sku: true, price: false },
                    note: `Price matched after backoff, SKU lag acceptable`
                  };
                }
                // Still mismatched - continue backoff loop
              }
            }
          } catch (backoffError) {
            console.warn(`⚠️ Backoff fetch ${backoffAttempt} failed:`, backoffError.message);
            // Continue to next backoff attempt
          }
        }

        // All backoff attempts exhausted - this is a true mismatch (likely ghost data from concurrent user)
        console.error('🚨 PRICE MISMATCH PERSISTS AFTER CDN BACKOFF - BLOCKING ORDER:', {
          variantId: variantId,
          expected: { sku: expectedSKU, price: expectedPrice },
          actual: { sku: actualSKU, price: actualPrice },
          mismatch: { sku: !skuMatch, price: !priceMatch },
          backoffAttempts: PROPAGATION_DELAYS.length - 1,
          totalBackoffTime: PROPAGATION_DELAYS.reduce((a, b) => a + b, 0)
        });

        this.logTelemetryEvent('price_mismatch_blocked_after_backoff', {
          variantId: variantId,
          expected: { sku: expectedSKU, price: expectedPrice },
          actual: { sku: actualSKU, price: actualPrice },
          duration: Date.now() - startTime,
          retryCount: retryCount,
          backoffAttempts: PROPAGATION_DELAYS.length - 1,
          critical: true
        });

        return {
          success: false, // BLOCK ORDER - PRICE MISMATCH persists after backoff
          actual: { sku: actualSKU, price: actualPrice },
          expected: { sku: expectedSKU, price: expectedPrice },
          mismatch: {
            sku: !skuMatch,
            price: !priceMatch
          },
          reason: 'PRICE mismatch persists after CDN backoff - blocking to prevent wrong billing'
        };
      }

    } catch (error) {
      const isTimeout = error.name === 'AbortError';
      const duration = Date.now() - startTime;

      console.warn(`⚠️ Verification ${isTimeout ? 'timeout' : 'error'} (${duration}ms):`, error.message);

      // Log telemetry
      this.logTelemetryEvent('verification_exception', {
        variantId: variantId,
        error: error.message,
        isTimeout: isTimeout,
        duration: duration,
        retryCount: retryCount
      });

      // Retry once
      if (retryCount < MAX_RETRIES) {
        console.log(`🔄 Retrying verification after ${isTimeout ? 'timeout' : 'error'}...`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return this.verifyVariantBeforeCart(variantId, productType, expectedSKU, expectedPrice, retryCount + 1);
      }

      // After retry: ALLOW ORDER (network error, not ghost data)
      console.warn('⚠️ Allowing order despite exception (network issue, not ghost data)');
      return {
        success: true, // ALLOW ORDER
        actual: { sku: 'error', price: 'error' },
        error: error.message,
        note: `Network ${isTimeout ? 'timeout' : 'error'} - allowing order to proceed`
      };
    }
  }

  // Add to cart
  async addToCart(variantId, quantity, properties, productType = 'glass', expectedPrice = null, isRecoveryAttempt = false, fullCalculatorData = null) {
    try {
      // Add ground truth properties for diagnostics and order audit trail.
      if (expectedPrice !== null) {
        properties['_Expected Price'] = Number(expectedPrice).toFixed(2);
      }
      if (fullCalculatorData && fullCalculatorData.expectedSKU) {
        properties['_Expected SKU'] = fullCalculatorData.expectedSKU;
      }

      // CRITICAL: Ensure _Product Title is present for cart fix scripts
      if (!properties['_Product Title']) {
        properties['_Product Title'] = productType === 'brick' ? 'Custom Cut Brick' : 'Custom Cut Stove Glass';
      }

      // Version stamp for diagnosing stale-cache incidents
      properties['_ClientPatchVersion'] = '2026-05-11-admin-validated-cart-add';

      const cleanProperties = this.filterCartProperties(properties, productType);

      if (this.debug) {
        this.debugLog('🛒 Adding to cart:', {
          variantId: variantId,
          quantity: quantity,
          cleanProperties: cleanProperties
        });
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
          properties: cleanProperties
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to add to cart: ${errorText}`);
      }

      const result = await response.json();

      if (expectedPrice !== null && !isRecoveryAttempt) {
        const expectedPricePounds = Number(expectedPrice);
        const returnedPricePence = Number(result.original_price ?? result.price);
        const returnedPricePounds = Number.isFinite(returnedPricePence) ? returnedPricePence / 100 : NaN;
        const priceMismatch = Number.isFinite(expectedPricePounds) &&
          Number.isFinite(returnedPricePounds) &&
          Math.abs(returnedPricePounds - expectedPricePounds) >= 0.01;
        const pennyPriceLeak = Number.isFinite(returnedPricePounds) && returnedPricePounds <= 0.01;

        if (priceMismatch || pennyPriceLeak) {
          console.error('🚨 POST-ADD PRICE GUARD BLOCKED CART ITEM', {
            variantId: variantId,
            itemKey: result.key,
            expectedPrice: expectedPricePounds,
            returnedPrice: returnedPricePounds,
            returnedSku: result.sku,
            expectedSku: fullCalculatorData && fullCalculatorData.expectedSKU ? fullCalculatorData.expectedSKU : null
          });

          this.logTelemetryEvent('post_add_price_guard_block', {
            variantId: variantId,
            itemKey: result.key || 'unknown',
            expectedPrice: expectedPricePounds.toFixed(2),
            returnedPrice: Number.isFinite(returnedPricePounds) ? returnedPricePounds.toFixed(2) : 'unknown',
            returnedSku: result.sku || 'unknown',
            expectedSku: fullCalculatorData && fullCalculatorData.expectedSKU ? fullCalculatorData.expectedSKU : 'unknown',
            reason: pennyPriceLeak ? 'penny_price_leak' : 'price_mismatch'
          });

          if (result.key) {
            const removeResponse = await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: result.key,
                quantity: 0
              })
            });

            if (!removeResponse.ok) {
              const removeError = await removeResponse.text();
              throw new Error(`POST_ADD_PRICE_GUARD_REMOVE_FAILED: ${removeError}`);
            }
          } else {
            throw new Error('POST_ADD_PRICE_GUARD_NO_ITEM_KEY');
          }

          throw new Error('POST_ADD_PRICE_GUARD_BLOCK');
        }
      }

      // Mark variant as in use
      this.setVariantState(variantId, this.STATES.IN_USE);

      // We only inspect /cart/add.js here. A separate /cart.js read can lag or
      // trigger rate limits, but the add response is the checkout-facing line.
      if (expectedPrice !== null && !isRecoveryAttempt) {
        this.logTelemetryEvent('post_add_cart_price_guard_passed', {
          variantId: variantId,
          expectedPrice: Number(expectedPrice).toFixed(2),
          expectedSKU: fullCalculatorData && fullCalculatorData.expectedSKU ? fullCalculatorData.expectedSKU : null,
          returnedPrice: Number(result.original_price ?? result.price) / 100
        });
      }

      this.debugLog('✅ Added to cart successfully');
      return result;

    } catch (error) {
      this.debugLog('❌ Error adding to cart:', error);
      throw error;
    }
  }

  // Generate titles and SKUs (same as original)
  generateTitle(calculatorData) {
    const { productType, shape, width, height, diameter, thickness, units } = calculatorData;

    // Debug logging to track title generation
    if (this.debug) {
      this.debugLog('🏷️ Generating title with data:', calculatorData);
      this.debugLog('🏷️ Shape value received:', shape);
    }

    if (productType === 'brick') {
      const shapeText = shape === 'shaped' ? 'Shaped' : 'Rectangular';
      return `Cut to Size ${thickness} ${shapeText} Brick ${width}×${height}mm`;
    } else {
      const shapeNames = {
        'square-rectangular': 'Rectangular',
        'arched': 'Arched',
        'half-arch': 'Half Arch',
        'both-top-corners-cut': 'Corner Cut',
        'single-corner-cut': 'Single Corner Cut',
        'all-corners-cut': 'All Corners Cut',
        'sloped-top': 'Sloped Top',
        'circular': 'Circular'
      };

      let title = shapeNames[shape] || 'Custom';

      if (shape === 'circular') {
        title += ` ${diameter}${units} Diameter`;
      } else {
        title += ` ${width}×${height}${units}`;
      }

      title += ` ${thickness} Cut to Size Glass`;
      return title;
    }
  }

  generateSKU(calculatorData) {
    const { productType, shape, width, height, diameter, thickness, units, ropeKit, gasket, inputC, inputD } = calculatorData;

    // Debug SKU generation for problematic cases
    if (this.debug) {
      this.debugLog('🔧 generateSKU input:', {
        productType, shape, width, height, diameter, thickness, units,
        ropeKit: ropeKit, gasket: gasket, inputC, inputD
      });
    }

    // Guard against incomplete data - return null instead of malformed SKU
    if (!shape || !thickness || (!width && !height && !diameter)) {
      this.debugLog('⚠️ generateSKU: Incomplete data detected, returning null', {
        shape: shape, thickness: thickness, width: width, height: height, diameter: diameter
      });
      this.incrementCounter('skuGenerationFailures', {
        reason: 'incomplete_data',
        missingFields: {
          shape: !shape,
          thickness: !thickness,
          dimensions: (!width && !height && !diameter)
        }
      });
      return null;
    }

    // Default units to 'mm' if missing
    const safeUnits = units || 'mm';

    // Helper to normalize fragments for SKU
    const norm = (val) => {
      if (val === undefined || val === null) return '';
      return String(val)
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^A-Za-z0-9-]/g, '-')
        .replace(/-+/g, '-')
        .toUpperCase();
    };

    // Helper to convert measurements to MM for SKU consistency
    const convertToMM = (value, unit) => {
      if (!value || value === 0) return 0;

      if (unit === 'mm') {
        return Math.round(value);
      } else if (unit === 'cm') {
        return Math.round(value * 10);
      } else if (unit === 'in') {
        return Math.round(value * 25.4);
      }
      return Math.round(value);
    };

    if (productType === 'brick') {
      // Only change SKU for custom-shaped (user uploads drawing)
      // Leave corner cuts alone - they use Cloudinary system properly
      // Use CUSTBRICK to distinguish from glass CUSTOM-RECT SKUs
      const shapeCode = shape === 'custom-shaped' ? 'CUSTBRICK' : 'RECT';
      return `BRICK-${norm(thickness)}-${shapeCode}-${width}x${height}`;
    } else {
      const parts = [];

      // Convert all measurements to MM for supplier consistency
      const widthMM = convertToMM(width, safeUnits);
      const heightMM = convertToMM(height, safeUnits);
      const diameterMM = convertToMM(diameter, safeUnits);
      const inputCMM = convertToMM(inputC, safeUnits);
      const inputDMM = convertToMM(inputD, safeUnits);

      // Additional guard: if all measurements convert to 0, return null
      if (widthMM === 0 && heightMM === 0 && diameterMM === 0) {
        this.debugLog('⚠️ generateSKU: All measurements converted to 0, returning null');
        return null;
      }

      // Debug converted measurements
      if (this.debug) {
        this.debugLog('🔧 Converted measurements:', {
          original: { width, height, diameter, inputC, inputD, units },
          converted: { widthMM, heightMM, diameterMM, inputCMM, inputDMM }
        });
      }

      // Shape-specific SKU generation with measurements in MM
      if (shape === 'square-rectangular') {
        parts.push('CUSTOM-RECT');
        parts.push(`W${widthMM}MM-H${heightMM}MMx${norm(thickness)}`);

      } else if (shape === 'arched') {
        parts.push('CUSTOM-ARCHED');
        parts.push(`W${widthMM}MM-H${heightMM}MMx${norm(thickness)}`);

      } else if (shape === 'half-arch') {
        parts.push('CUSTOM-HALF-ARCH');
        parts.push(`W${widthMM}MM-H${heightMM}MMx${norm(thickness)}`);

      } else if (shape === 'both-top-corners-cut') {
        parts.push('CUSTOM-BOTH-TOP-CORNERS-CUT');
        parts.push(`W${widthMM}MM-H${heightMM}MM-HEIGHT-TO-CUT-OFF-${inputCMM}MM-TOP-FLAT-${inputDMM}MMx${norm(thickness)}`);

      } else if (shape === 'single-corner-cut') {
        parts.push('CUSTOM-SINGLE-CORNER-CUT');
        parts.push(`W${widthMM}MM-H${heightMM}MM-HEIGHT-TO-CUT-OFF-${inputCMM}MM-TOP-FLAT-${inputDMM}MMx${norm(thickness)}`);

      } else if (shape === 'all-corners-cut') {
        parts.push('CUSTOM-ALL-CORNERS-CUT');
        parts.push(`W${widthMM}MM-H${heightMM}MM-CORNER-CUT-VERTICAL-${inputCMM}MM-CORNER-CUT-HORIZONTAL-${inputDMM}MMx${norm(thickness)}`);

      } else if (shape === 'sloped-top') {
        parts.push('CUSTOM-SLOPED-TOP');
        parts.push(`BOTTOM-W${widthMM}MM-LEFT-H${heightMM}MM-RIGHT-H${inputCMM}MMx${norm(thickness)}`);

      } else if (shape === 'trapezium') {
        parts.push('CUSTOM-TRAPEZIUM');
        parts.push(`BOTTOMW${widthMM}MM-TOPW${inputCMM}MM-H${heightMM}MMx${norm(thickness)}`);

      } else if (shape === 'circular') {
        parts.push('CUSTOM-CIRCULAR');
        parts.push(`D${diameterMM}MMx${norm(thickness)}`);

      } else {
        // Fallback for unknown shapes
        parts.push('CUSTOM-SHAPE');
        parts.push(`${widthMM}MMx${heightMM}MMx${norm(thickness)}`);
      }

      // Accessories in SKU for supplier visibility
      if (ropeKit && ropeKit.selected) {
        const ropeColor = norm(ropeKit.color || '');
        const ropeDia = ropeKit.diameterMm ? `${parseInt(ropeKit.diameterMm, 10)}MM` : '';

        // Build rope kit part: COLOR-ROPE-KIT-DIAMETER (SOFT prefix only for non-3MM)
        const isSoftRope = ropeDia !== '3MM';
        if (ropeColor && ropeDia) {
          parts.push(isSoftRope ? `SOFT-${ropeColor}-ROPE-KIT-${ropeDia}` : `${ropeColor}-ROPE-KIT-${ropeDia}`);
        } else if (ropeColor) {
          parts.push(`SOFT-${ropeColor}-ROPE-KIT`);
        } else {
          parts.push('ROPE-KIT');
        }
      }

      if (gasket && gasket.selected) {
        const gasketColor = norm(gasket.color || '');
        // Default to 2 meters if length not provided
        const lengthMeters = (gasket.lengthMeters != null && !isNaN(gasket.lengthMeters)) ? Number(gasket.lengthMeters) : 2;

        // Special handling for gaskets with size specifications
        if (gasketColor === 'BLACK') {
          parts.push('10MM-BLACK-GASKET');
          parts.push(`${lengthMeters}-METERS`);
        } else if (gasketColor === 'WHITE') {
          parts.push('12MM-OR-15MM-WHITE-GASKET');
          parts.push(`${lengthMeters}-METERS`);
        } else {
          // Standard gasket handling for other colors
          if (gasketColor) parts.push(gasketColor);
          parts.push('GASKET');
          parts.push(`${lengthMeters}-METERS`);
        }
      }

      // Join with hyphens and ensure no accidental double hyphens
      return parts
        .filter(Boolean)
        .join('-')
        .replace(/-+/g, '-');
    }
  }

  // Enhanced processOrder with forced reset and validation
  async processOrder(calculatorData, fallbackFunction) {
    const {
      itemPrice,
      quantity,
      properties,
      productType = 'glass',
      shape,
      width,
      height,
      diameter,
      thickness,
      units,
      ropeKit,
      gasket,
      inputC = 0,
      inputD = 0
    } = calculatorData;

    if (this.debug) {
      this.debugLog('🚀 Starting Enhanced Variant Pool process...');
      this.debugLog('🚀 Calculator data:', calculatorData);
    }

    // Hard fail-closed on invalid pricing to prevent £0.01/£0.00 orders
    const normalizedPrice = Number(itemPrice);
    if (!Number.isFinite(normalizedPrice) || normalizedPrice <= 0.01) {
      this.logTelemetryEvent('invalid_price_blocked', {
        productType: calculatorData.productType || 'unknown',
        itemPrice: itemPrice,
        normalizedPrice: normalizedPrice
      });
      throw new Error('INVALID_PRICE');
    }

    this.setProductType(productType);

    // Try to find the correct button elements (glass or brick calculator)
    const button = document.querySelector('#custom-add-to-cart-button') || document.querySelector('#brick-add-to-cart-button');
    const buttonText = document.querySelector('#custom-add-to-cart-text') || document.querySelector('#brick-add-to-cart-text');

    if (buttonText) buttonText.textContent = 'Creating Your Order...';
    if (button) button.disabled = true;

    let variantId;
    let singleFlightOwner = null;

    try {
      // Global single-flight lock to prevent duplicate add-to-cart operations from
      // duplicated script initialization or rapid re-entry.
      singleFlightOwner = this.acquireSingleFlightLock(productType);

      // Step 1: Find available CLEAN variant  
      if (this.debug) this.debugLog('🔍 Step 1: Finding available CLEAN variant...');
      variantId = await this.findAvailableVariant(); // Changed to 'let' to allow reassignment in retry logic

      // CRITICAL: Double-check pending state (findAvailableVariant already marked it, but safety first)
      if (this.pendingVariants.has(variantId)) {
        // This is expected now, but we'll continue
      } else {
        this.pendingVariants.add(variantId);
      }

      // Mark variant as being processed immediately to prevent race conditions
      this.setVariantState(variantId, this.STATES.PENDING_CLEANUP);

      // Step 2: Generate product details
      if (this.debug) this.debugLog('📝 Step 2: Generating product details...');
      // Use simple customer-friendly title instead of complex shape-based titles
      const title = productType === 'brick' ? 'Custom Cut Brick' : 'Custom Cut Glass';

      // Extract inputC and inputD from calculatorData
      const { inputC, inputD, supplierSpecUrl } = calculatorData;

      // Always generate descriptive SKU (no character limit issues)
      const sku = this.generateSKU({ productType, shape, width, height, diameter, thickness, units, ropeKit, gasket, inputC, inputD });

      // Guard against null SKU - use safe fallback if generateSKU returns null
      const safeSKU = sku || `CUSTOM-${productType.toUpperCase()}-${Date.now()}`;

      // Inject expected SKU into calculator data for audit trail
      calculatorData.expectedSKU = safeSKU;
      if (!sku) {
        this.debugLog('⚠️ generateSKU returned null, using fallback SKU:', safeSKU);
        this.logTelemetryEvent('sku_fallback_used', {
          productType: productType,
          shape: shape,
          fallbackSKU: safeSKU
        });
      }

      // If we have a Cloudinary URL, we'll add it to cart properties instead of SKU
      let specImageProperty = null;
      if (supplierSpecUrl) {
        specImageProperty = supplierSpecUrl;
        this.incrementCounter('cloudinaryUrls', {
          shape: shape,
          url: supplierSpecUrl
        });
        this.logTelemetryEvent('cloudinary_sku_used', {
          shape: shape,
          url: supplierSpecUrl,
          variantId: variantId,
          method: 'cart_properties'
        });
      }

      // Safety check: only throw if safeSKU itself is unavailable (which shouldn't happen)
      if (!safeSKU) {
        this.debugLog('❌ SKU generation failed critically in processOrder, triggering fallback');
        throw new Error('SKU generation failed - incomplete calculator data');
      }

      if (buttonText) buttonText.textContent = 'Updating Product...';

      // Step 3: SAFE UPDATE - update to customer details with validation
      if (this.debug) this.debugLog('📡 Step 3: Safe update to customer details...');

      // NEW: Client-side pricing validation
      // This prevents accidental bugs from sending invalid prices to the server
      try {
        this.validatePricing(itemPrice, thickness, { width, height, diameter, shape });
      } catch (validationError) {
        console.error('🚨 PRICING VALIDATION FAILED:', validationError.message);
        this.logTelemetryEvent('pricing_validation_failed', {
          error: validationError.message,
          price: itemPrice,
          thickness: thickness,
          dimensions: { width, height, diameter }
        });
        throw validationError; // Block the order
      }

      // Directly update to customer details (server will overwrite price+sku atomically)
      // Webhook will reset variant post-purchase to default title/price/sku
      // IMPORTANT: Netlify now returns validated SKU/price after read-after-write verification
      const netlifyResult = await this.updateVariantDetails(variantId, {
        title: title,
        price: itemPrice,
        sku: safeSKU
      });

      // Step 3.5: TRUST SERVER VALIDATION (No slow CDN verification needed!)
      // Netlify has already done read-after-write validation via Admin API (authoritative source)
      // The Admin API response is the ground truth - no CDN lag issues
      if (this.debug) this.debugLog('🔍 Step 3.5: Using server-validated data (no CDN fetch needed)');

      const expectedSKU = safeSKU;
      const expectedPrice = parseFloat(itemPrice.toFixed(2));

      // Build verification result from Netlify's validated response
      // This is much faster than fetching from CDN which has propagation lag
      let verificationResult;

      if (netlifyResult && netlifyResult.validated) {
        // Server confirmed the update via Admin API read-after-write
        const serverSKU = netlifyResult.validated.sku;
        const serverPrice = netlifyResult.validated.price;

        const skuMatch = serverSKU === expectedSKU;
        const priceMatch = Math.abs(serverPrice - expectedPrice) < 0.01;

        if (skuMatch && priceMatch) {
          console.log('✅ SERVER VALIDATION PASSED (Admin API read-after-write)', {
            variantId: variantId,
            sku: serverSKU,
            price: serverPrice
          });
          verificationResult = {
            success: true,
            actual: { sku: serverSKU, price: serverPrice },
            source: 'netlify_validated'
          };
        } else {
          // This should be very rare - server says it updated but values don't match what we sent
          console.warn('⚠️ Server validation mismatch (unexpected):', {
            expected: { sku: expectedSKU, price: expectedPrice },
            server: { sku: serverSKU, price: serverPrice },
            skuMatch: skuMatch,
            priceMatch: priceMatch,
            priceDiff: Math.abs(serverPrice - expectedPrice),
            variantId: variantId
          });

          // Log to telemetry for monitoring
          this.logTelemetryEvent('server_validation_mismatch_details', {
            variantId: variantId,
            expectedSKU: expectedSKU,
            serverSKU: serverSKU,
            expectedPrice: expectedPrice,
            serverPrice: serverPrice,
            skuMatch: skuMatch,
            priceMatch: priceMatch
          });

          verificationResult = {
            success: false,
            actual: { sku: serverSKU, price: serverPrice },
            expected: { sku: expectedSKU, price: expectedPrice },
            source: 'netlify_mismatch'
          };
        }
      } else {
        // Older Netlify response format without validated field - fall back to trusting success
        console.log('✅ SERVER UPDATE SUCCEEDED (legacy response format)', { variantId });
        verificationResult = {
          success: true,
          actual: { sku: expectedSKU, price: expectedPrice },
          source: 'netlify_success_legacy'
        };
      }

      // NEW: Check for FALLBACK_REQUIRED - verification failed due to network issues
      // Instead of proceeding with potentially wrong pricing, use penny-based fallback
      if (verificationResult.success === 'FALLBACK_REQUIRED') {
        console.warn('⚠️ FALLBACK_REQUIRED - Using penny-based fallback for correct pricing');
        this.logTelemetryEvent('fallback_triggered', {
          variantId: variantId,
          productType: productType,
          reason: verificationResult.reason,
          expectedPrice: expectedPrice
        });

        // Release the pending variant since we're not using it
        this.pendingVariants.delete(variantId);
        this.setVariantState(variantId, this.STATES.CLEAN);

        // Reset button state
        if (buttonText) buttonText.textContent = 'Processing (Fallback)...';

        // Call the fallback function passed from calculator
        if (typeof fallbackFunction === 'function') {
          return fallbackFunction();
        } else {
          throw new Error('Fallback function not available');
        }
      }

      if (!verificationResult.success) {
        // This should be very rare with server validation - only happens if Netlify returns
        // different values than what we sent (possible race condition with another user)
        console.error('🚨 SERVER VALIDATION MISMATCH', verificationResult);

        // Quarantine the mismatched variant
        this.setVariantState(variantId, this.STATES.QUARANTINED);

        // Log to telemetry
        this.logTelemetryEvent('server_validation_failed', {
          variantId: variantId,
          productType: productType,
          expected: { sku: expectedSKU, price: expectedPrice },
          actual: verificationResult.actual,
          source: verificationResult.source
        });

        // Retry with fresh variant - server validation should succeed
        if (this.debug) this.debugLog('🔄 Retrying with fresh variant due to server validation mismatch...');
        const freshVariantId = await this.findAvailableVariant(productType);

        // Update the fresh variant and get server validation
        const retryNetlifyResult = await this.updateVariantDetails(freshVariantId, {
          title: title,
          price: itemPrice,
          sku: safeSKU
        });

        // Build retry verification from server response (no CDN fetch needed)
        let retryVerification;
        if (retryNetlifyResult && retryNetlifyResult.validated) {
          const serverSKU = retryNetlifyResult.validated.sku;
          const serverPrice = retryNetlifyResult.validated.price;
          const skuMatch = serverSKU === expectedSKU;
          const priceMatch = Math.abs(serverPrice - expectedPrice) < 0.01;

          retryVerification = {
            success: skuMatch && priceMatch,
            actual: { sku: serverSKU, price: serverPrice }
          };
        } else {
          // Legacy response - trust success
          retryVerification = {
            success: true,
            actual: { sku: expectedSKU, price: expectedPrice }
          };
        }

        if (!retryVerification.success) {
          console.error('🚨 SERVER VALIDATION FAILED ON RETRY - Using fallback');
          // Use fallback instead of blocking completely
          if (typeof fallbackFunction === 'function') {
            this.pendingVariants.delete(freshVariantId);
            this.setVariantState(freshVariantId, this.STATES.CLEAN);
            if (buttonText) buttonText.textContent = 'Processing (Fallback)...';
            return fallbackFunction();
          } else {
            throw new Error(`Server validation failed twice: Expected SKU ${expectedSKU} but got ${retryVerification.actual?.sku || 'unknown'}`);
          }
        }

        // Release old pending variant before switching to the fresh one.
        this.pendingVariants.delete(variantId);

        // Use fresh variant
        variantId = freshVariantId;
        console.log('✅ Retry server validation passed, using fresh variant', freshVariantId);
      } else {
        console.log('✅ SERVER VALIDATION PASSED', {
          variantId: variantId,
          sku: verificationResult.actual.sku,
          price: verificationResult.actual.price,
          source: verificationResult.source
        });
      }

      // Step 4: Add to cart after Admin API read-after-write validation.
      // Avoid public cart verification here because it can lag the Admin API update.
      if (this.debug) this.debugLog('🛒 Step 4: Adding to cart...');

      // Add Cloudinary URL to properties if available (with cleaner labeling for supplier)
      if (specImageProperty) {
        // Hidden in cart, visible in admin/emails
        properties['_View Drawing'] = specImageProperty;
        this.debugLog('📷 Added Cloudinary URL to cart properties (prefixed):', specImageProperty);

        // Build human-readable shape description for suppliers (no letter references)
        const width = calculatorData.width || 0;
        const height = calculatorData.height || 0;
        const thickness = calculatorData.thickness || '4mm';
        const inputC = calculatorData.inputC || 0;
        const inputD = calculatorData.inputD || 0;
        const diameter = calculatorData.diameter || 0;
        const shape = calculatorData.shape;

        let shapeDescription = '';

        switch (shape) {
          case 'square-rectangular':
            shapeDescription = `Rectangle`;
            break;

          case 'both-top-corners-cut':
            shapeDescription = `Both Top Corners Cut`;
            break;

          case 'single-corner-cut':
            shapeDescription = `Single Corner Cut`;
            break;

          case 'all-corners-cut':
            shapeDescription = `All Corners Cut`;
            break;

          case 'arched':
            shapeDescription = `Arched Top`;
            break;

          case 'half-arch':
            shapeDescription = `Half Arch`;
            break;

          case 'sloped-top':
            shapeDescription = `Sloped Top`;
            break;

          case 'circular':
            shapeDescription = `Circular`;
            break;

          default:
            // Fallback for unknown shapes
            shapeDescription = shape.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
        }

        properties['Shape Details'] = shapeDescription;
      }

      // CRITICAL SAFETY CHECK: Block cart add if Netlify update wasn't confirmed
      // This prevents £0.01 orders when Netlify request fails silently (network issues, timeouts)
      if (!netlifyResult || !netlifyResult.success) {
        console.error('🚨 PRE-CART SAFETY CHECK FAILED - Netlify update not confirmed', {
          netlifyResult: netlifyResult,
          variantId: variantId,
          expectedPrice: itemPrice,
          expectedSKU: safeSKU
        });

        this.logTelemetryEvent('pre_cart_safety_block', {
          variantId: variantId,
          productType: productType,
          reason: 'netlify_update_not_confirmed',
          netlifyResult: netlifyResult ? JSON.stringify(netlifyResult) : 'null'
        });

        throw new Error('PRE_CART_SAFETY_BLOCK');
      }

      // HARD GUARD: Never add to cart if server-validated state looks like an unconfigured pool variant
      // or penny price while a custom SKU was expected.
      const expectedCustomSku = typeof safeSKU === 'string' ? safeSKU : '';
      const expectedCustom = expectedCustomSku.startsWith('CUSTOM-');
      const verifiedSku = (verificationResult && verificationResult.actual && verificationResult.actual.sku)
        ? String(verificationResult.actual.sku)
        : '';
      const verifiedPrice = Number(
        verificationResult && verificationResult.actual ? verificationResult.actual.price : NaN
      );
      const upperVerifiedSku = verifiedSku.toUpperCase();
      const looksLikePoolSku = /^POOL-\d{3}$/.test(upperVerifiedSku) || /^BRICK-POOL-\d+/.test(upperVerifiedSku);
      const looksLikePennyPrice = Number.isFinite(verifiedPrice) && verifiedPrice <= 0.01;
      const missingValidatedPayload = expectedCustom && (!netlifyResult || !netlifyResult.validated);

      if ((expectedCustom && (looksLikePoolSku || looksLikePennyPrice)) || missingValidatedPayload) {
        this.logTelemetryEvent('pre_cart_pool_guard_block', {
          variantId: variantId,
          expectedSKU: expectedCustomSku,
          verifiedSKU: verifiedSku || 'unknown',
          verifiedPrice: Number.isFinite(verifiedPrice) ? verifiedPrice : 'unknown',
          missingValidatedPayload: missingValidatedPayload
        });
        throw new Error('PRE_CART_POOL_GUARD_BLOCK');
      }

      // Diagnostic: record which validation path was used and what code path reached addToCart
      properties['_ValidationSource'] = verificationResult.source || 'unknown';
      properties['_AddPath'] = 'processOrder';

      await this.addToCart(variantId, quantity, properties, productType, itemPrice, false, calculatorData);

      // Mark variant as CLEAN immediately after successful cart add so that
      // cleanupAbandonedVariants does not treat it as "stuck" and reset it
      // while the customer is still browsing before checkout.
      // The orders/create webhook will handle the actual reset after purchase.
      this.setVariantState(variantId, this.STATES.CLEAN);

      // Success!
      if (this.debug) this.debugLog('✅ Success! Redirecting to cart...');
      if (buttonText) buttonText.textContent = 'Added to Cart!';

      // Track successful order
      this.incrementCounter('successfulOrders', {
        variantId: variantId,
        productType: productType,
        shape: shape,
        usedCloudinaryUrl: !!supplierSpecUrl,
        price: itemPrice
      });
      this.logTelemetryEvent('order_success', {
        variantId: variantId,
        productType: productType,
        finalSKU: safeSKU,
        sessionDuration: Date.now() - this.telemetry.startTime
      });

      setTimeout(() => {
        window.location.href = '/cart';
      }, 500);

    } catch (error) {
      this.debugLog('❌ Enhanced variant pool failed:', error);
      console.error('❌ DETAILED ERROR INFO:', {
        error: error,
        message: error.message,
        stack: error.stack,
        step: 'Unknown - check which step failed above'
      });

      // Track failed order
      this.incrementCounter('failedOrders', {
        productType: productType,
        shape: shape,
        errorType: error.name,
        errorMessage: error.message
      });
      this.logTelemetryEvent('order_failure', {
        productType: productType,
        errorType: error.name,
        errorMessage: error.message,
        calculatorData: calculatorData,
        sessionDuration: Date.now() - this.telemetry.startTime
      });

      if (this.debug) {
        this.debugLog('🔄 Attempting fallback to original method...');
      }

      const blockFallbackErrors = new Set([
        'INVALID_PRICE',
        'PRE_CART_SAFETY_BLOCK',
        'PRE_CART_POOL_GUARD_BLOCK',
        'ORDER_IN_FLIGHT'
      ]);

      if (blockFallbackErrors.has(error.message) || String(error.message || '').startsWith('POST_ADD_PRICE_GUARD')) {
        if (buttonText) buttonText.textContent = 'Add to Cart';
        if (button) button.disabled = false;
        if (error.message === 'ORDER_IN_FLIGHT') {
          alert('Order is already being processed. Please wait a moment.');
        } else {
          alert('Pricing error detected. Please recalculate and try again.');
        }
        return;
      }

      if (this.fallbackToCart && fallbackFunction) {
        this.debugLog('Falling back to original cart method...');
        if (buttonText) buttonText.textContent = 'Using Fallback...';

        // Track fallback usage
        this.incrementCounter('fallbacks', {
          productType: productType,
          fallbackMethod: 'original_cart',
          errorType: error.name
        });
        this.logTelemetryEvent('fallback_used', {
          fallbackMethod: 'original_cart',
          errorType: error.name,
          errorMessage: error.message,
          productType: productType
        });

        // Send fallback beacon for monitoring
        this.sendFallbackBeacon({
          error: error.message,
          poolType: this.poolVariants === this.glassPoolVariants ? 'glass' : 'brick',
          variantId: variantId,
          fallbackMethod: 'original_cart',
          calculatorData: {
            ...calculatorData,
            expectedPrice: itemPrice,
            expectedSKU: calculatorData.expectedSKU || null
          }
        });

        fallbackFunction();
      } else {
        if (buttonText) buttonText.textContent = 'Error!';
        if (button) button.disabled = false;

        setTimeout(() => {
          if (buttonText) buttonText.textContent = 'Add to Cart';
        }, 2000);

        alert('Unable to process order. Please try again.');
      }
    } finally {
      // CRITICAL: Always remove from pending set, even on error or fallback
      // This ensures the variant becomes available again for next customer
      if (typeof variantId !== 'undefined' && variantId) {
        this.pendingVariants.delete(variantId);
        if (this.debug) {
          this.debugLog(`🔓 Released variant ${variantId} from pending state`);
        }
      }

      // Always release global single-flight lock
      this.releaseSingleFlightLock(singleFlightOwner);
    }
  }

  // Send fallback beacon to server for monitoring
  async sendFallbackBeacon(data) {
    try {
      const beaconData = {
        operation: 'report-error',
        error: data.error,
        poolType: data.poolType,
        variantId: data.variantId || 'unknown',
        shape: data.calculatorData?.shape || 'unknown',
        dimensions: {
          width: data.calculatorData?.width || 0,
          height: data.calculatorData?.height || 0,
          thickness: data.calculatorData?.thickness || 'unknown',
          quantity: data.calculatorData?.quantity || 1,
          expectedPrice: data.calculatorData?.expectedPrice || 'unknown',
          expectedSKU: data.calculatorData?.expectedSKU || 'unknown',
          actualPrice: data.calculatorData?.actualPrice || 'unknown',
          actualSKU: data.calculatorData?.actualSKU || 'unknown'
        },
        fallbackMethod: data.fallbackMethod,
        cartUrl: window.location.href,
        timestamp: new Date().toISOString()
      };

      // Send beacon (non-blocking, short timeout)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

      await fetch(this.updateEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(beaconData),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (this.debug) {
        this.debugLog('📊 Fallback beacon sent successfully');
      }
    } catch (beaconError) {
      // Don't fail the main flow if beacon fails
      if (this.debug) {
        this.debugLog('⚠️ Failed to send fallback beacon:', beaconError);
      }
    }
  }

  // Get pool health status
  getPoolHealth() {
    const health = {
      total: this.poolVariants.length,
      clean: 0,
      inUse: 0,
      pendingCleanup: 0,
      quarantined: 0
    };

    this.poolVariants.forEach(variantId => {
      const state = this.getVariantState(variantId);
      switch (state) {
        case this.STATES.CLEAN:
          health.clean++;
          break;
        case this.STATES.IN_USE:
          health.inUse++;
          break;
        case this.STATES.PENDING_CLEANUP:
          health.pendingCleanup++;
          break;
        case this.STATES.QUARANTINED:
          health.quarantined++;
          break;
      }
    });

    return health;
  }
}

// Shared recovery helpers for calculator pages (glass + brick restore sizing
// from an expired pool cart line without retyping). Calculators call
// window.SGRRecovery.consumeRestore() on start.
window.SGRRecovery = (function () {
  const RECOVERY_KEY = 'sgr_expired_recovery_v1';
  const RESTORE_KEY = 'sgr_restore_request_v1';

  function readStore() {
    try { return JSON.parse(localStorage.getItem(RECOVERY_KEY) || '[]'); }
    catch (e) { return []; }
  }

  function parseNum(str) {
    if (str === undefined || str === null) return 0;
    const m = String(str).match(/[\d.]+/);
    return m ? parseFloat(m[0]) : 0;
  }

  function pickProp(props, candidates) {
    for (const k of candidates) {
      if (props[k] !== undefined && props[k] !== null && String(props[k]).trim() !== '') return String(props[k]);
    }
    // Fuzzy fallback: key contains candidate text
    const keys = Object.keys(props);
    for (const c of candidates) {
      const hit = keys.find(k => k.toLowerCase().includes(c.toLowerCase()));
      if (hit && String(props[hit]).trim() !== '') return String(props[hit]);
    }
    return '';
  }

  function toSpec(snap) {
    const p = (snap && snap.properties) || {};
    const glassType = p['Glass Type'] || p['Brick Type'] || '';
    const thickMatch = (glassType + ' ' + (p['Thickness'] || '')).match(/(\d+)\s*mm/i);
    const spec = {
      productType: snap.productType || 'glass',
      shape: p['_Internal_Shape'] || p['_Internal shape'] || '',
      units: p['_Internal_Units'] || p['_Internal units'] || 'mm',
      width: parseNum(pickProp(p, ['Overall Width', 'Width', 'Bottom Width'])),
      height: parseNum(pickProp(p, ['Overall Height', 'Height', 'Left Height'])),
      inputC: parseNum(pickProp(p, ['Top Width', 'Height to Cut-off', 'Height to Cut-Off', 'Corner Cut Vertical', 'Right Height', 'Corner Cut C', 'Input C'])),
      inputD: parseNum(pickProp(p, ['Top Flat Width', 'Top Flat', 'Corner Cut Horizontal', 'Corner Cut D', 'Input D'])),
      diameter: parseNum(pickProp(p, ['Diameter'])),
      thickness: thickMatch ? thickMatch[1] + 'mm' : (p['Thickness'] || ''),
      quantity: parseInt(p['_Quantity'] || p['Quantity'] || snap.quantity || 1, 10) || 1
    };
    // Brick is always mm
    if (spec.productType === 'brick') spec.units = 'mm';
    return spec;
  }

  return {
    consumeRestore: function () {
      try {
        const raw = localStorage.getItem(RESTORE_KEY);
        if (!raw) return null;
        localStorage.removeItem(RESTORE_KEY);
        const snap = JSON.parse(raw);
        return { snapshot: snap, spec: toSpec(snap) };
      } catch (e) { return null; }
    },
    peekRestore: function () {
      try {
        const raw = localStorage.getItem(RESTORE_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (e) { return null; }
    }
  };
})();

// Export for calculator
window.SimpleVariantPool = SimpleVariantPool;
